import { useCustomerRegistryRefresh } from '../utils/customerRegistryEvents';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AIInsightsPanel from '../components/AIInsightsPanel';
import {
  FaPlus,
  FaSave,
  FaTrash,
  FaEdit,
  FaSync,
  FaFileImport,
  FaFileDownload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUniversity,
  FaMoneyCheckAlt,
  FaKeyboard,
  FaTags,
  FaSitemap,
  FaChartPie,
  FaTimes,
  FaBuilding,
  FaSearch,
  FaFileExcel,
  FaCog,
  FaFileInvoice,
  FaReceipt,
  FaClipboardList,
  FaInfoCircle,
  FaCalendarAlt,
  FaBell,
  FaLock,
} from 'react-icons/fa';
import XLSX from '@redoper1/xlsx-js-style';
import FinanceService from '../services/FinanceService';
import api from '../api';
import { sendForecastReminderFlow } from '../services/forecastReminderFlow';
import { useAuth } from '../AuthContext';
import {
  FINANCE_MANAGE_PERMISSION,
  FINANCE_VIEW_PERMISSION,
  hasPermission,
} from '../utils/permissions';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import ExpensesManager from '../components/ExpensesManager';
import OpenFinanceConnectionStatus from '../components/OpenFinanceConnectionStatus';
import HumanResourcesService from '../services/HumanResourcesService';
import AssetService from '../services/AssetService';
import { createPortal } from 'react-dom';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const DEFAULT_CATEGORY_COLORS = [
  '#0ea5e9',
  '#10b981',
  '#f97316',
  '#6366f1',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#14b8a6',
  '#ec4899',
  '#3b82f6',
];

const getColorByIndex = (preferredColor, index) => {
  if (typeof preferredColor === 'string' && preferredColor.trim()) {
    return preferredColor.trim();
  }
  return DEFAULT_CATEGORY_COLORS[index % DEFAULT_CATEGORY_COLORS.length];
};

const INTEGRATION_TYPES = [
  { value: 'NONE', label: 'Lançamentos manuais (sem integração)' },
  { value: 'OFX', label: 'Importação via OFX' },
  { value: 'API', label: 'Integração automática — Open Finance ou credenciais' },
];

const MANUAL_TRANSACTION_TYPES = [
  { value: 'CREDIT', label: 'Crédito' },
  { value: 'DEBIT', label: 'Débito' },
];

const emptyManualTransactionForm = {
  type: 'DEBIT',
  amount: '',
  occurredAt: '',
  description: '',
  notes: '',
  categoryId: '',
  subcategoryId: '',
  documentNumber: '',
};

const formatLocalDateTimeInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (value) => String(value).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const parseAmountInput = (rawValue) => {
  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return rawValue;
  }
  if (rawValue === null || rawValue === undefined) {
    return Number.NaN;
  }
  const trimmed = String(rawValue).trim();
  if (!trimmed) {
    return Number.NaN;
  }
  const normalizedWhitespace = trimmed.replace(/[^\d,.-]/g, '').replace(/\s+/g, '');
  const hasComma = normalizedWhitespace.includes(',');
  const hasDot = normalizedWhitespace.includes('.');
  let normalized = normalizedWhitespace;
  if (hasComma && hasDot) {
    normalized = normalized.replace(/\./g, '').replace(/,/g, '.');
  } else if (hasComma) {
    normalized = normalized.replace(/,/g, '.');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const maskDigits = (value, pattern) => {
  const digits = String(value || '').replace(/\D/g, '');
  let result = '';
  let index = 0;
  for (const char of pattern) {
    if (index >= digits.length) break;
    if (char === '#') {
      result += digits[index];
      index += 1;
    } else {
      result += char;
    }
  }
  return result;
};

const maskCnpj = (value) => String(value || '').replace(/\D/g, '').length <= 11 ? maskDigits(value, '###.###.###-##') : maskDigits(value, '##.###.###/####-##');
const maskCep = (value) => maskDigits(value, '#####-###');
const maskPhone = (value) => {
  const raw = String(value || '').replace(/\D/g, '').slice(0, 13);
  if (raw.startsWith('55') && raw.length > 11) return '+55 ' + maskPhone(raw.slice(2));
  const digits = raw.slice(0, 11);
  return digits.length > 10 ? maskDigits(digits, '(##) #####-####') : maskDigits(digits, '(##) ####-####');
};

const emptyCompanyForm = {
  id: null,
  name: '',
  legalName: '',
  cnpj: '',
  zipCode: '',
  address: '', number: '', complement: '', neighborhood: '', city: '', state: '',
  phones: [''],
  emails: [''],
  notes: '', status: 'ACTIVE',
};

const normalizeCompanyForForm = (company = {}) => ({
  id: company.id ?? null,
  name: company.name || '',
  legalName: company.legalName || '',
  cnpj: maskCnpj(company.cnpj || ''),
  zipCode: maskCep(company.zipCode || ''),
  address: company.address || '', number: company.number || '', complement: company.complement || '',
  neighborhood: company.neighborhood || '', city: company.city || '', state: company.state || '',
  phones: String(company.phones || '').split(/[;,]/).map((item) => maskPhone(item.trim())).filter(Boolean).concat(['']).slice(0, 51),
  emails: String(company.emails || '').split(/[;,]/).map((item) => item.trim()).filter(Boolean).concat(['']).slice(0, 51),
  notes: company.notes || '', status: company.status || 'ACTIVE',
});

const serializeCompanyForm = (form) => ({
  name: form.name.trim(),
  legalName: form.legalName.trim() || null,
  cnpj: maskCnpj(form.cnpj),
  zipCode: maskCep(form.zipCode),
  address: form.address.trim() || null, number: form.number?.trim() || null, complement: form.complement?.trim() || null,
  neighborhood: form.neighborhood?.trim() || null, city: form.city?.trim() || null, state: form.state?.trim().toUpperCase() || null,
  phones: (form.phones || []).map((item) => maskPhone(item)).filter(Boolean).join('; '),
  emails: (form.emails || []).map((item) => item.trim()).filter(Boolean).join('; '),
  notes: form.notes.trim() || null, status: form.status || 'ACTIVE',
});

const emptyForm = {
  id: null,
  name: '',
  bankName: '',
  agencyNumber: '',
  accountNumber: '',
  description: '',
  integrationType: 'NONE',
  originalIntegrationType: 'NONE',
  integrationSyncStartAt: '',
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
};

const formatCurrencyInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return formatCurrency(Number(digits) / 100);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const resolveErrorMessage = (error) => {
  if (!error) {
    return 'Ocorreu um erro inesperado.';
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (typeof error.message === 'string') {
    return error.message;
  }
  return 'Ocorreu um erro inesperado.';
};

const toFormState = (account) => {
  if (!account) {
    return { ...emptyForm };
  }
  return {
    id: account.id ?? null,
    name: account.name ?? '',
    bankName: account.bankName ?? '',
    agencyNumber: account.agencyNumber ?? '',
    accountNumber: account.accountNumber ?? '',
    description: account.description ?? '',
    integrationType: account.integrationType ?? 'NONE',
    originalIntegrationType: account.integrationType ?? 'NONE',
    integrationSyncStartAt: account.integrationSyncStartAt
      ? String(account.integrationSyncStartAt).slice(0, 10)
      : '',
  };
};



const downloadWorkbook = (workbook, filename) => {
  XLSX.writeFile(workbook, filename);
};

const buildImportTemplateWorkbook = () => {
  const rows = [
    ['Data', 'Descrição', 'Valor', 'Tipo', 'Nº documento', 'Categoria', 'Subcategoria', 'Observações'],
    ['2026-06-28', 'Pagamento fornecedor exemplo', '-1500,00', 'DEBIT', 'NF-12345', 'Administrativo', 'Serviços', 'Use CREDIT ou DEBIT'],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 14 }, { wch: 34 }, { wch: 14 }, { wch: 12 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 36 }];
  rows[0].forEach((_, index) => {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c: index })];
    cell.s = { fill: { fgColor: { rgb: '064E3B' } }, font: { color: { rgb: 'FFFFFF' }, bold: true }, alignment: { horizontal: 'center' } };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Importação');
  return wb;
};

const STATUS_STYLES = {
  PENDING: 'bg-amber-100 text-amber-700',
  RECONCILED: 'bg-emerald-100 text-emerald-700',
  DIVERGENT: 'bg-rose-100 text-rose-700',
  CLEARED: 'bg-slate-100 text-slate-600',
};

const STATUS_LABELS = {
  PENDING: 'Pendente',
  RECONCILED: 'Conciliada',
  DIVERGENT: 'Divergente',
  CLEARED: 'Compensada',
};

const TRANSACTION_TYPE_LABELS = {
  CREDIT: 'Crédito',
  DEBIT: 'Débito',
};

const FINANCE_TABS = [
  { value: 'categorySummary', label: 'Resumo por categoria' },
  { value: 'transactions', label: 'Transações' },
  { value: 'accountability', label: 'Prestação de contas' },
  { value: 'expenseForecast', label: 'Previsão de gastos' },
  { value: 'insights', label: 'Insights IA' },
  { value: 'settings', label: 'Configurações' },
];

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'NFE', label: 'Nota fiscal eletrônica' },
  { value: 'HOLERITE', label: 'Holerite' },
  { value: 'FATURA', label: 'Nota/fatura' },
  { value: 'ENCARGOS', label: 'Guia de encargos' },
  { value: 'RECIBO', label: 'Recibo' },
  { value: 'OUTRO', label: 'Outro documento' },
];

const TRANSACTION_COLUMN_OPTIONS = [
  ['occurredAt', 'Data'], ['description', 'Descrição'], ['documentNumber', 'Nº documento'], ['amount', 'Valor'], ['type', 'Tipo'], ['source', 'Origem'], ['category', 'Categoria'], ['subcategory', 'Subcategoria'],
];

const SOURCE_LABELS = {
  OFX: 'OFX',
  INTEGRATION: 'Integração',
  MANUAL: 'Manual',
};

const sortAccountsByName = (accounts) =>
  [...accounts].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));

const mergeCategoryReports = (reports) => {
  const categoriesMap = new Map();
  const addAmount = (value) => Number(value || 0);

  (reports || []).forEach((report) => {
    (report?.categories || []).forEach((category) => {
      const categoryKey = category.id === null || category.id === undefined ? '__none__' : String(category.id);
      if (!categoriesMap.has(categoryKey)) {
        categoriesMap.set(categoryKey, {
          id: category.id ?? null,
          name: category.name || 'Sem categoria',
          color: category.color ?? null,
          transactionCount: 0,
          totalCredits: 0,
          totalDebits: 0,
          balance: 0,
          subcategories: [],
        });
      }
      const targetCategory = categoriesMap.get(categoryKey);
      targetCategory.transactionCount += addAmount(category.transactionCount);
      targetCategory.totalCredits += addAmount(category.totalCredits);
      targetCategory.totalDebits += addAmount(category.totalDebits);
      targetCategory.balance += addAmount(category.balance);

      (category.subcategories || []).forEach((subcategory) => {
        const subcategoryKey = subcategory.id === null || subcategory.id === undefined ? '__none__' : String(subcategory.id);
        let targetSubcategory = targetCategory.subcategories.find(
          (entry) => (entry.id === null || entry.id === undefined ? '__none__' : String(entry.id)) === subcategoryKey,
        );
        if (!targetSubcategory) {
          targetSubcategory = {
            id: subcategory.id ?? null,
            name: subcategory.name || 'Sem subcategoria',
            color: subcategory.color ?? category.color ?? null,
            transactionCount: 0,
            totalCredits: 0,
            totalDebits: 0,
            balance: 0,
          };
          targetCategory.subcategories.push(targetSubcategory);
        }
        targetSubcategory.transactionCount += addAmount(subcategory.transactionCount);
        targetSubcategory.totalCredits += addAmount(subcategory.totalCredits);
        targetSubcategory.totalDebits += addAmount(subcategory.totalDebits);
        targetSubcategory.balance += addAmount(subcategory.balance);
      });
    });
  });

  const categories = Array.from(categoriesMap.values())
    .map((category) => ({
      ...category,
      transactionCount: Number(category.transactionCount || 0),
      totalCredits: Number(category.totalCredits.toFixed(2)),
      totalDebits: Number(category.totalDebits.toFixed(2)),
      balance: Number(category.balance.toFixed(2)),
      subcategories: category.subcategories
        .map((subcategory) => ({
          ...subcategory,
          transactionCount: Number(subcategory.transactionCount || 0),
          totalCredits: Number(subcategory.totalCredits.toFixed(2)),
          totalDebits: Number(subcategory.totalDebits.toFixed(2)),
          balance: Number(subcategory.balance.toFixed(2)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));

  return {
    categories,
    totalCredits: Number(categories.reduce((acc, item) => acc + item.totalCredits, 0).toFixed(2)),
    totalDebits: Number(categories.reduce((acc, item) => acc + item.totalDebits, 0).toFixed(2)),
    balance: Number(categories.reduce((acc, item) => acc + item.balance, 0).toFixed(2)),
    generatedAt: new Date().toISOString(),
  };
};


const FORECAST_STORAGE_KEY = 'finance_expense_forecast_v1';
const FORECAST_REMINDER_LOG_KEY = 'finance_expense_forecast_email_reminder_log_v1';

const HR_BENEFIT_CATALOG_STORAGE_KEY = 'humanResourcesBenefitCatalog';
const HR_BENEFIT_EXCLUSIONS_STORAGE_KEY = 'humanResourcesBenefitExclusions';

const loadStoredJson = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || JSON.stringify(fallback));
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const normalizeBenefitSignature = (benefit) => `${String(benefit?.name || '').trim().toLowerCase()}|${formatCurrencyInput(String(benefit?.monthlyCost || ''))}`;

const normalizeHrBenefitCatalog = (items) => (Array.isArray(items) ? items : [])
  .map((item) => ({
    id: String(item?.id || ''),
    name: String(item?.name || '').trim(),
    monthlyCost: item?.monthlyCost || '',
    employmentTypes: Array.isArray(item?.employmentTypes) ? item.employmentTypes : [],
  }))
  .filter((item) => item.name && item.employmentTypes.length);

const loadHrBenefitCatalog = () => normalizeHrBenefitCatalog(loadStoredJson(HR_BENEFIT_CATALOG_STORAGE_KEY, []));

const loadHrBenefitExclusions = () => {
  const parsed = loadStoredJson(HR_BENEFIT_EXCLUSIONS_STORAGE_KEY, {});
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  return Object.fromEntries(Object.entries(parsed).map(([employeeId, ids]) => [String(employeeId), Array.isArray(ids) ? ids.map(String) : []]));
};

const calculateCatalogAwareBenefitsMonthlyCost = (employees, catalog, exclusions) => (Array.isArray(employees) ? employees : [])
  .filter((employee) => employee?.status !== 'TERMINATED')
  .reduce((total, employee) => {
    const existingBenefits = Array.isArray(employee?.benefits) ? employee.benefits : [];
    const existingSignatures = new Set(existingBenefits.map(normalizeBenefitSignature).filter((signature) => signature !== '|'));
    const employeeExclusions = new Set((exclusions[String(employee?.id)] || []).map(String));
    const existingBenefitsTotal = existingBenefits.reduce((sum, benefit) => sum + normalizeCurrencyNumber(benefit?.monthlyCost), 0);
    const catalogBenefitsTotal = catalog
      .filter((benefit) => benefit.employmentTypes.includes(employee?.employmentType || ''))
      .filter((benefit) => !employeeExclusions.has(String(benefit.id)) && !employeeExclusions.has(normalizeBenefitSignature(benefit)))
      .filter((benefit) => !existingSignatures.has(normalizeBenefitSignature(benefit)))
      .reduce((sum, benefit) => sum + normalizeCurrencyNumber(benefit.monthlyCost), 0);
    return total + existingBenefitsTotal + catalogBenefitsTotal;
  }, 0);

const createEmptyForecastExpense = () => ({
  id: null,
  name: '',
  description: '',
  amount: '',
  date: '',
  recurring: false,
  recurrenceInterval: 1,
  recurrenceUnit: 'months',
  recurrenceAnchorDate: '',
  reminderEnabled: false,
  reminderDaysBefore: 2,
  targetUserIds: [],
  targetSectorIds: [],
  absencePeriods: [],
  attachments: [],
});

const recurrenceUnitLabels = {
  days: 'dia(s)',
  weeks: 'semana(s)',
  months: 'mês(es)',
  years: 'ano(s)',
};

const normalizeCurrencyNumber = (value) => {
  const parsed = parseAmountInput(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const maskForecastCurrency = (value) => formatCurrencyInput(value);

const formatForecastAmountForForm = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const numericValue = typeof value === 'number' ? value : normalizeCurrencyNumber(value);
  if (!Number.isFinite(numericValue)) return '';
  return formatCurrency(numericValue);
};

const formatDateOnlyLabel = (value) => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date);
};

const formatMonthLabel = (value) => {
  if (!value) return '-';
  const date = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(date);
};

const parseForecastDate = (value) => {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  const raw = String(value).trim();
  const brMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) {
    const [, day, month, year] = brMatch;
    return new Date(`${year}-${month}-${day}T00:00:00`);
  }
  return new Date(raw.length === 7 ? `${raw}-01T00:00:00` : raw);
};

const getMonthKey = (value) => {
  const date = parseForecastDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const extractAssetList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const addForecastInterval = (date, interval, unit) => {
  const next = new Date(date);
  const safeInterval = Number(interval) || 1;
  if (unit === 'days') next.setDate(next.getDate() + safeInterval);
  if (unit === 'weeks') next.setDate(next.getDate() + (safeInterval * 7));
  if (unit === 'months') next.setMonth(next.getMonth() + safeInterval);
  if (unit === 'years') next.setFullYear(next.getFullYear() + safeInterval);
  return next;
};

const resolveOverviewSummary = (overview) => overview?.summary || overview?.data?.summary || overview || {};

const getReminderDateForOccurrence = (occurrenceDate, daysBefore) => {
  const date = parseForecastDate(occurrenceDate);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() - (Number(daysBefore) || 0));
  return date.toISOString().slice(0, 10);
};

const readForecastAttachmentFiles = (fileList) => Promise.all(
  Array.from(fileList || []).map((file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      type: file.type || 'application/octet-stream',
      size: file.size || 0,
      dataUrl: reader.result,
      uploadedAt: new Date().toISOString(),
    });
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  })),
).then((items) => items.filter(Boolean));

function ExpenseForecastPanel({ canManage, showAlert }) {
  const [forecastExpenses, setForecastExpenses] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(FORECAST_STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  });
  const [forecastForm, setForecastForm] = useState(createEmptyForecastExpense);
  const [integrationValues, setIntegrationValues] = useState({
    hrMonthlyCost: 0,
    benefitsMonthlyCost: 0,
    scheduledMaintenance: [],
    loading: true,
  });
  const [selectedForecastMonth, setSelectedForecastMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [reminderUsers, setReminderUsers] = useState([]);
  const [reminderSectors, setReminderSectors] = useState([]);
  const [showForecastForm, setShowForecastForm] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(FORECAST_STORAGE_KEY, JSON.stringify(forecastExpenses));
    } catch (error) {
      showAlert?.('error', 'Não foi possível gravar a previsão no armazenamento do navegador. Remova anexos muito grandes ou tente arquivos menores.');
    }
  }, [forecastExpenses, showAlert]);

  useEffect(() => {
    let active = true;
    api.get('/calendar/recipients').then((response) => {
      if (!active) return;
      setReminderUsers(Array.isArray(response.data?.users) ? response.data.users : []);
      setReminderSectors(Array.isArray(response.data?.sectors) ? response.data.sectors : []);
    }).catch(() => {
      if (!active) return;
      setReminderUsers([]);
      setReminderSectors([]);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const loadIntegrations = async () => {
      try {
        const [overview, employees, assets] = await Promise.all([
          HumanResourcesService.getOverview().catch(() => null),
          HumanResourcesService.listEmployees({ status: 'CURRENT' }).catch(() => []),
          AssetService.listAssets().catch(() => []),
        ]);
        if (!active) return;
        const overviewSummary = resolveOverviewSummary(overview);
        const assetList = extractAssetList(assets);
        const scheduledMaintenance = assetList.flatMap((asset) => {
          const maintenance = Array.isArray(asset?.maintenance) ? asset.maintenance : [];
          return maintenance
            .filter((entry) => entry?.status !== 'COMPLETED' && entry?.status !== 'CANCELLED')
            .map((entry) => ({
              ...entry,
              assetName: asset?.name || asset?.code || 'Ativo',
              monthKey: getMonthKey(entry?.scheduledAt || entry?.performedAt),
              amount: normalizeCurrencyNumber(entry?.cost),
            }));
        });
        const backendBenefitsMonthlyCost = normalizeCurrencyNumber(overviewSummary.benefitsMonthlyCost);
        const catalogAwareBenefitsMonthlyCost = calculateCatalogAwareBenefitsMonthlyCost(
          employees,
          loadHrBenefitCatalog(),
          loadHrBenefitExclusions()
        );
        const benefitsMonthlyCost = Math.max(backendBenefitsMonthlyCost, catalogAwareBenefitsMonthlyCost);
        setIntegrationValues({
          hrMonthlyCost: normalizeCurrencyNumber(overviewSummary.hrMonthlyCost) + Math.max(0, benefitsMonthlyCost - backendBenefitsMonthlyCost),
          benefitsMonthlyCost,
          scheduledMaintenance,
          loading: false,
        });
      } catch {
        if (active) setIntegrationValues((prev) => ({ ...prev, loading: false }));
      }
    };
    loadIntegrations();
    return () => { active = false; };
  }, []);

  const selectedMonthManualExpenses = useMemo(() => {
    const results = [];
    forecastExpenses.forEach((item) => {
      if (!item.recurring) {
        if (getMonthKey(item.date) === selectedForecastMonth) results.push({ ...item, occurrenceDate: item.date });
        return;
      }
      const startValue = item.recurrenceAnchorDate || item.date;
      const startDate = new Date(`${startValue}T00:00:00`);
      if (Number.isNaN(startDate.getTime())) return;
      const monthStart = new Date(`${selectedForecastMonth}-01T00:00:00`);
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0, 23, 59, 59);
      let occurrenceDate = startDate;
      let guard = 0;
      while (occurrenceDate <= monthEnd && guard < 600) {
        if (occurrenceDate >= monthStart && getMonthKey(occurrenceDate) === selectedForecastMonth) {
          const occurrenceDateText = occurrenceDate.toISOString().slice(0, 10);
          const isAbsent = (item.absencePeriods || []).some((period) => {
            const start = period?.startDate || period?.start || '';
            const end = period?.endDate || period?.end || '';
            return start && end && occurrenceDateText >= start && occurrenceDateText <= end;
          });
          if (!isAbsent) results.push({ ...item, occurrenceDate: occurrenceDateText });
        }
        occurrenceDate = addForecastInterval(occurrenceDate, item.recurrenceInterval, item.recurrenceUnit);
        guard += 1;
      }
    });
    return results;
  }, [forecastExpenses, selectedForecastMonth]);

  const selectedMonthMaintenance = useMemo(
    () => (integrationValues.scheduledMaintenance || []).filter((entry) => entry.monthKey === selectedForecastMonth),
    [integrationValues.scheduledMaintenance, selectedForecastMonth],
  );

  const manualForecastTotal = selectedMonthManualExpenses.reduce((total, item) => total + normalizeCurrencyNumber(item.amount), 0);
  const scheduledMaintenanceCost = selectedMonthMaintenance.reduce((total, item) => total + normalizeCurrencyNumber(item.amount), 0);
  const integratedTotal = integrationValues.hrMonthlyCost + integrationValues.benefitsMonthlyCost + scheduledMaintenanceCost;
  const grandTotal = manualForecastTotal + integratedTotal;

  const syncForecastEmailReminders = useCallback(async (entries) => {
    const today = new Date().toISOString().slice(0, 10);
    let log = {};
    try { log = JSON.parse(localStorage.getItem(FORECAST_REMINDER_LOG_KEY) || '{}'); } catch { log = {}; }
    for (const entry of entries) {
      if (!entry?.reminderEnabled) continue;
      const hasSystemRecipients = (entry.targetUserIds || []).length > 0 || (entry.targetSectorIds || []).length > 0;
      if (!hasSystemRecipients) continue;
      const occurrences = [];
      if (!entry.recurring) {
        occurrences.push(entry.date);
      } else {
        let occurrenceDate = parseForecastDate(entry.recurrenceAnchorDate || entry.date);
        let guard = 0;
        while (!Number.isNaN(occurrenceDate.getTime()) && guard < 24) {
          occurrences.push(occurrenceDate.toISOString().slice(0, 10));
          occurrenceDate = addForecastInterval(occurrenceDate, entry.recurrenceInterval, entry.recurrenceUnit);
          guard += 1;
        }
      }
      for (const occurrenceDate of occurrences) {
        const isAbsent = (entry.absencePeriods || []).some((period) => {
          const start = period?.startDate || period?.start || '';
          const end = period?.endDate || period?.end || '';
          return start && end && occurrenceDate >= start && occurrenceDate <= end;
        });
        if (isAbsent) continue;
        const reminderDate = getReminderDateForOccurrence(occurrenceDate, entry.reminderDaysBefore);
        const key = `${entry.id}-${occurrenceDate}-${reminderDate}-${(entry.targetUserIds || []).join(',')}-${(entry.targetSectorIds || []).join(',')}`;
        if (reminderDate !== today || log[key]) continue;
        try {
          if (hasSystemRecipients) {
            await sendForecastReminderFlow({
              api,
              entry,
              occurrenceDate,
              reminderDate,
              formatCurrency,
              formatDateOnlyLabel,
            });
          }
          log[key] = true;
          localStorage.setItem(FORECAST_REMINDER_LOG_KEY, JSON.stringify(log));
        } catch (error) {
          showAlert?.('error', error?.response?.data?.error || error?.message || 'Falha ao enviar o lembrete financeiro. A tentativa será repetida quando a aba for aberta novamente.');
        }
      }
    }
  }, [showAlert]);

  useEffect(() => {
    syncForecastEmailReminders(forecastExpenses);
  }, [forecastExpenses, syncForecastEmailReminders]);

  const handleForecastSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    const invalidAbsencePeriod = (forecastForm.absencePeriods || []).some((period) => period?.startDate && period?.endDate && period.endDate < period.startDate);
    if (invalidAbsencePeriod) {
      showAlert?.('error', 'A data de fim da ausência não pode ser menor que a data de início.');
      return;
    }
    if (forecastForm.reminderEnabled && !(forecastForm.targetUserIds || []).length && !(forecastForm.targetSectorIds || []).length) {
      showAlert?.('error', 'Selecione pelo menos um usuário ou setor para receber o lembrete.');
      return;
    }
    const entry = {
      ...forecastForm,
      id: forecastForm.id || Date.now(),
      amount: normalizeCurrencyNumber(forecastForm.amount),
      absencePeriods: (forecastForm.absencePeriods || []).filter((period) => period?.startDate && period?.endDate),
      recurrenceAnchorDate: forecastForm.recurring ? (forecastForm.recurrenceAnchorDate || forecastForm.date) : '',
      reminderDaysBefore: Number(forecastForm.reminderDaysBefore || 0),
    };
    const buildNext = (prev) => (forecastForm.id ? prev.map((item) => (item.id === forecastForm.id ? entry : item)) : [entry, ...prev]);
    setForecastExpenses((prev) => {
      const next = buildNext(prev);
      syncForecastEmailReminders(next);
      return next;
    });
    setSelectedForecastMonth(getMonthKey(entry.date) || selectedForecastMonth);
    setForecastForm(createEmptyForecastExpense());
    setShowForecastForm(false);
    showAlert?.('success', 'Previsão de gasto salva. Os destinatários selecionados serão notificados no sistema e por e-mail.');
  };

  return (
    <section className="space-y-5">
      {integrationValues.loading && (
        <div className="overflow-hidden rounded-full bg-emerald-100 shadow-inner" aria-label="Carregando previsão de gastos">
          <div className="h-2 w-2/3 animate-pulse rounded-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
        </div>
      )}
      <div className="overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-800 via-teal-700 to-cyan-700 p-5 text-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide"><FaCalendarAlt /> Planejamento financeiro</span>
            <h2 className="mt-3 text-4xl font-extrabold tracking-tight">Previsão de gastos</h2>{canManage && <button type="button" onClick={() => { setForecastForm(createEmptyForecastExpense()); setShowForecastForm(true); }} className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold text-emerald-700 shadow-sm hover:bg-emerald-50"><FaPlus /> Nova despesa prevista</button>}
          </div>
          <div className="rounded-3xl border border-white/30 bg-white/20 p-4 text-right shadow-lg ring-1 ring-white/20"><label className="block text-sm font-bold uppercase tracking-wide text-white">Mês da previsão<input type="month" value={selectedForecastMonth} onChange={(e) => setSelectedForecastMonth(e.target.value)} className="mt-2 block w-full rounded-2xl border-2 border-white bg-white px-4 py-3 text-base font-semibold text-slate-900 shadow-sm" /></label><div className="mt-3 rounded-2xl bg-white/15 px-5 py-4"><p className="text-xs text-emerald-50">Total previsto em {formatMonthLabel(selectedForecastMonth)}</p><p className="text-2xl font-bold">{formatCurrency(grandTotal)}</p></div></div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-2xl border bg-white p-4 shadow-sm"><p className="text-xs font-semibold uppercase text-slate-500">Despesas do mês</p><p className="mt-2 text-xl font-bold text-slate-900">{formatCurrency(manualForecastTotal)}</p></div>
        <div className="rounded-2xl border bg-white p-4 shadow-sm"><p className="text-xs font-semibold uppercase text-slate-500">Custo RH Mensal</p><p className="mt-2 text-xl font-bold text-emerald-700">{integrationValues.loading ? '…' : formatCurrency(integrationValues.hrMonthlyCost)}</p></div>
        <div className="rounded-2xl border bg-white p-4 shadow-sm"><p className="text-xs font-semibold uppercase text-slate-500">Custo Benefícios Mensal</p><p className="mt-2 text-xl font-bold text-cyan-700">{integrationValues.loading ? '…' : formatCurrency(integrationValues.benefitsMonthlyCost)}</p></div>
        <div className="rounded-2xl border bg-white p-4 shadow-sm"><p className="text-xs font-semibold uppercase text-slate-500">Manutenções programadas</p><p className="mt-2 text-xl font-bold text-amber-700">{integrationValues.loading ? '…' : formatCurrency(scheduledMaintenanceCost)}</p></div>
      </div>

      <div className="space-y-5">
        {showForecastForm && createPortal((<div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4"><div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl"><form onSubmit={handleForecastSubmit} className="space-y-3">
          <div className="flex items-center justify-between gap-3"><h3 className="text-2xl font-bold text-slate-900">{forecastForm.id ? 'Editar despesa' : 'Nova despesa prevista'}</h3><div className="flex gap-2">{forecastForm.id && <button type="button" onClick={() => setForecastForm(createEmptyForecastExpense())} className="rounded-full border px-3 py-1 text-xs text-slate-600">Novo</button>}<button type="button" onClick={() => { setForecastForm(createEmptyForecastExpense()); setShowForecastForm(false); }} className="rounded-full border px-3 py-1 text-xs text-slate-600">Fechar</button></div></div>
          <input required disabled={!canManage} value={forecastForm.name} onChange={(e) => setForecastForm({ ...forecastForm, name: e.target.value })} placeholder="Nome da despesa" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <textarea disabled={!canManage} value={forecastForm.description} onChange={(e) => setForecastForm({ ...forecastForm, description: e.target.value })} placeholder="Descrição" rows={3} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <div className="grid gap-3 sm:grid-cols-2"><input required disabled={!canManage} value={forecastForm.amount} onChange={(e) => setForecastForm({ ...forecastForm, amount: maskForecastCurrency(e.target.value) })} placeholder="R$ 0,00" inputMode="numeric" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" /><input required type="date" disabled={!canManage} value={forecastForm.date} onChange={(e) => setForecastForm({ ...forecastForm, date: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" /></div><div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-3"><label className="text-xs font-semibold uppercase text-slate-500">Documento opcional<input type="file" multiple disabled={!canManage} onChange={async (e) => { const files = await readForecastAttachmentFiles(e.target.files); setForecastForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...files] })); e.target.value = ''; }} className="mt-2 block w-full text-sm font-normal normal-case text-slate-600" /></label>{(forecastForm.attachments || []).length > 0 && <div className="mt-3 space-y-2">{forecastForm.attachments.map((attachment) => <div key={attachment.id || attachment.name} className="flex items-center justify-between gap-2 rounded-xl bg-white px-3 py-2 text-xs text-slate-600"><span className="truncate">{attachment.name}</span><div className="flex shrink-0 gap-2">{attachment.dataUrl && <a href={attachment.dataUrl} download={attachment.name || 'documento-despesa'} className="rounded-full border border-emerald-200 px-2 py-1 text-emerald-700">Baixar</a>}<button type="button" onClick={() => setForecastForm((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((item) => item !== attachment) }))} className="rounded-full border border-rose-200 px-2 py-1 text-rose-600">Remover</button></div></div>)}</div>}</div>
          <label className="flex items-center gap-2 rounded-xl bg-slate-50 p-3 text-sm"><input type="checkbox" disabled={!canManage} checked={forecastForm.recurring} onChange={(e) => setForecastForm({ ...forecastForm, recurring: e.target.checked })} />Despesa recorrente</label>
          {forecastForm.recurring && <div className="space-y-3 rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/60 p-3"><div className="grid gap-3 sm:grid-cols-3"><input type="number" min="1" value={forecastForm.recurrenceInterval} onChange={(e) => setForecastForm({ ...forecastForm, recurrenceInterval: Number(e.target.value || 1) })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" /><select value={forecastForm.recurrenceUnit} onChange={(e) => setForecastForm({ ...forecastForm, recurrenceUnit: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm"><option value="days">Dias</option><option value="weeks">Semanas</option><option value="months">Meses</option><option value="years">Anos</option></select><input type="date" value={forecastForm.recurrenceAnchorDate || forecastForm.date} onChange={(e) => setForecastForm({ ...forecastForm, recurrenceAnchorDate: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" /></div><button type="button" disabled={!canManage} onClick={() => setForecastForm({ ...forecastForm, absencePeriods: [...(forecastForm.absencePeriods || []), { startDate: '', endDate: '' }] })} className="rounded-full border border-emerald-200 bg-white px-4 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">Adicionar período de ausência de despesa</button>{(forecastForm.absencePeriods || []).map((period, index) => <div key={`absence-${index}`} className="grid gap-2 rounded-xl bg-white p-3 sm:grid-cols-[1fr_1fr_auto]"><label className="text-xs font-semibold uppercase text-slate-500">Início<input type="date" value={period.startDate || ''} onChange={(e) => setForecastForm({ ...forecastForm, absencePeriods: (forecastForm.absencePeriods || []).map((item, itemIndex) => itemIndex === index ? { ...item, startDate: e.target.value } : item) })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-normal normal-case" /></label><label className="text-xs font-semibold uppercase text-slate-500">Fim<input type="date" min={period.startDate || undefined} value={period.endDate || ''} onChange={(e) => setForecastForm({ ...forecastForm, absencePeriods: (forecastForm.absencePeriods || []).map((item, itemIndex) => itemIndex === index ? { ...item, endDate: e.target.value } : item) })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-normal normal-case" /></label><button type="button" onClick={() => setForecastForm({ ...forecastForm, absencePeriods: (forecastForm.absencePeriods || []).filter((_, itemIndex) => itemIndex !== index) })} className="self-end rounded-full border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600">Remover</button></div>)}</div>}
          <label className="flex items-center gap-2 rounded-xl bg-slate-50 p-3 text-sm"><input type="checkbox" disabled={!canManage} checked={forecastForm.reminderEnabled} onChange={(e) => setForecastForm({ ...forecastForm, reminderEnabled: e.target.checked })} /><FaBell className="text-emerald-600" />Adicionar lembrete</label>
          {forecastForm.reminderEnabled && <div className="grid gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-3 sm:grid-cols-3"><label className="space-y-1 text-xs font-semibold uppercase text-slate-500">Usuários que receberão<select multiple value={forecastForm.targetUserIds || []} onChange={(e) => setForecastForm({ ...forecastForm, targetUserIds: Array.from(e.target.selectedOptions).map((option) => Number(option.value)) })} className="mt-1 h-28 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-normal normal-case">{reminderUsers.map((user) => <option key={user.id} value={user.id}>{user.name || user.email}</option>)}</select></label><label className="space-y-1 text-xs font-semibold uppercase text-slate-500">Setores que receberão<select multiple value={forecastForm.targetSectorIds || []} onChange={(e) => setForecastForm({ ...forecastForm, targetSectorIds: Array.from(e.target.selectedOptions).map((option) => Number(option.value)) })} className="mt-1 h-28 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-normal normal-case">{reminderSectors.map((sector) => <option key={sector.id} value={sector.id}>{sector.name}</option>)}</select></label><label className="space-y-1 text-xs font-semibold uppercase text-slate-500">Lembrar quantos dias antes?<input type="number" min="0" placeholder="Ex.: 2" value={forecastForm.reminderDaysBefore} onChange={(e) => setForecastForm({ ...forecastForm, reminderDaysBefore: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-normal normal-case" title="Quantidade de dias antes do vencimento para notificar no sistema e por e-mail" /><small className="mt-2 block text-[11px] normal-case text-slate-500">Os selecionados serão notificados no sistema e por e-mail.</small></label></div>}
          {canManage && <button className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white"><FaSave /> Salvar previsão</button>}
        </form></div></div>), document.body)}
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <h3 className="text-2xl font-extrabold text-slate-900">Despesas de {formatMonthLabel(selectedForecastMonth)}</h3>
          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100">{selectedMonthManualExpenses.length ? selectedMonthManualExpenses.map((item, index) => <div key={`${item.id}-${item.occurrenceDate || item.date}`} className={`grid gap-3 border-b border-slate-100 p-4 last:border-b-0 md:grid-cols-[1fr_auto] ${index % 2 === 0 ? 'bg-white' : 'bg-emerald-50/60'}` }><div className="flex flex-wrap items-start justify-between gap-3 md:contents"><div><p className="font-semibold text-slate-900">{item.name}</p><p className="text-sm text-slate-500">{item.description || 'Sem descrição'}</p><p className="mt-1 text-xs text-slate-500">Data: {formatDateOnlyLabel(item.occurrenceDate || item.date)}{item.recurring ? ` • Recorrência: a cada ${item.recurrenceInterval} ${recurrenceUnitLabels[item.recurrenceUnit] || item.recurrenceUnit} desde ${formatDateOnlyLabel(item.recurrenceAnchorDate || item.date)}` : ''}</p>{item.reminderEnabled && <p className="mt-1 text-xs text-emerald-700">Lembrete: {item.reminderDaysBefore} dia(s) antes</p>}{item.recurring && (item.absencePeriods || []).length > 0 && <p className="mt-1 text-xs text-amber-700">Ausências cadastradas: {(item.absencePeriods || []).length}</p>}{(item.attachments || []).length > 0 && <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500"><span>Anexo(s):</span>{(item.attachments || []).map((attachment) => attachment.dataUrl ? <a key={attachment.id || attachment.name} href={attachment.dataUrl} download={attachment.name || 'documento-despesa'} className="font-semibold text-emerald-700 underline">{attachment.name || 'Documento'}</a> : <span key={attachment.id || attachment.name}>{attachment.name || 'Documento'}</span>)}</div>}</div><div className="text-right"><p className="font-bold text-rose-600">{formatCurrency(item.amount)}</p>{canManage && <div className="mt-2 flex gap-2"><button type="button" onClick={() => { setForecastForm({ ...createEmptyForecastExpense(), ...item, amount: formatForecastAmountForForm(item.amount) }); setShowForecastForm(true); }} className="rounded-full border px-3 py-1 text-xs text-emerald-700">Editar</button><button type="button" onClick={() => setForecastExpenses((prev) => prev.filter((entry) => entry.id !== item.id))} className="rounded-full border border-rose-200 px-3 py-1 text-xs text-rose-600">Excluir</button></div>}</div></div></div>) : <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">Nenhuma despesa prevista para este mês.</div>}</div>
          {selectedMonthMaintenance.length > 0 && (
            <div className="mt-5 rounded-2xl border border-amber-100 bg-amber-50/70 p-4">
              <h4 className="font-semibold text-amber-900">Manutenções programadas em {formatMonthLabel(selectedForecastMonth)}</h4>
              <div className="mt-3 space-y-2">
                {selectedMonthMaintenance.map((entry) => (
                  <div key={entry.id || `${entry.assetName}-${entry.scheduledAt}`} className="flex items-center justify-between rounded-xl bg-white px-3 py-2 text-sm">
                    <span className="text-slate-700">{entry.assetName} • {formatDateOnlyLabel(entry.scheduledAt || entry.performedAt)}</span>
                    <strong className="text-amber-700">{formatCurrency(entry.amount)}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const buildDateParam = (value, endOfDay = false) => {
  if (!value) return undefined;
  const suffix = endOfDay ? 'T23:59:59' : 'T00:00:00';
  const date = new Date(`${value}${suffix}`);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
};

export default function Finance() {
  const { user } = useAuth();
  const isDarkTheme =
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const canView =
    hasPermission(user, FINANCE_VIEW_PERMISSION) || hasPermission(user, FINANCE_MANAGE_PERMISSION);
  const canManage = hasPermission(user, FINANCE_MANAGE_PERMISSION);

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState({ ...emptyForm });
  const [formError, setFormError] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);
  const [deletingAccountId, setDeletingAccountId] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState('');
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPagination, setTransactionPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });

  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);
  const [markOthersAsDivergent, setMarkOthersAsDivergent] = useState(false);
  const [reconciliationNotes, setReconciliationNotes] = useState('');
  const [reconciling, setReconciling] = useState(false);

  const [importingOfx, setImportingOfx] = useState(false);
  const [syncingAccount, setSyncingAccount] = useState(false);
  const [openFinanceStatusVersion, setOpenFinanceStatusVersion] = useState(0);
  const [openFinanceAutoStartAccountId, setOpenFinanceAutoStartAccountId] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [showManualTransactionForm, setShowManualTransactionForm] = useState(false);
  const [manualFormState, setManualFormState] = useState(() => ({
    ...emptyManualTransactionForm,
    occurredAt: formatLocalDateTimeInput(new Date()),
  }));
  const [manualFormError, setManualFormError] = useState('');
  const [creatingManualTransaction, setCreatingManualTransaction] = useState(false);
  const [deletingTransactionIds, setDeletingTransactionIds] = useState([]);

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [subcategoryError, setSubcategoryError] = useState('');
  const [categoryFormState, setCategoryFormState] = useState({
    name: '',
    color: '',
    description: '',
  });
  const [subcategoryFormState, setSubcategoryFormState] = useState({
    categoryId: '',
    name: '',
    color: '',
    description: '',
  });
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [subcategorySubmitting, setSubcategorySubmitting] = useState(false);
  const [categoryDeletingId, setCategoryDeletingId] = useState(null);
  const [subcategoryDeletingId, setSubcategoryDeletingId] = useState(null);
  const [categoryUpdatingId, setCategoryUpdatingId] = useState(null);
  const [subcategoryUpdatingId, setSubcategoryUpdatingId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const [showCategoryReport, setShowCategoryReport] = useState(false);
  const [categoryReportData, setCategoryReportData] = useState(null);
  const [categoryReportLoading, setCategoryReportLoading] = useState(false);
  const [categoryReportError, setCategoryReportError] = useState('');

  const [showCategoryManager, setShowCategoryManager] = useState(false);

  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [editingTransactionDescription, setEditingTransactionDescription] = useState('');
  const [editingTransactionError, setEditingTransactionError] = useState('');
  const [updatingTransaction, setUpdatingTransaction] = useState(false);
  const [editingTransactionCategoryId, setEditingTransactionCategoryId] = useState('');
  const [editingTransactionSubcategoryId, setEditingTransactionSubcategoryId] = useState('');
  const [editingTransactionDocumentNumber, setEditingTransactionDocumentNumber] = useState('');

  const [alert, setAlert] = useState(null);
  const [activeFinanceTab, setActiveFinanceTab] = useState('categorySummary');
  const [settingsSection, setSettingsSection] = useState('columns');
  const [companies, setCompanies] = useState([]);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [savingFinanceSettings, setSavingFinanceSettings] = useState(false);
  const [selectedAccountabilityTransactionId, setSelectedAccountabilityTransactionId] = useState(null);
  const [accountabilityKind, setAccountabilityKind] = useState('entries');
  const [accountability, setAccountability] = useState(() => { try { return JSON.parse(localStorage.getItem('financeAccountability') || '{}'); } catch { return {}; } });
  const [visibleTransactionColumns, setVisibleTransactionColumns] = useState(() => { try { return JSON.parse(localStorage.getItem('financeTransactionColumns') || 'null') || TRANSACTION_COLUMN_OPTIONS.map(([key]) => key); } catch { return TRANSACTION_COLUMN_OPTIONS.map(([key]) => key); } });

  const fileInputRef = useRef(null);

  useEffect(() => { localStorage.setItem('financeAccountability', JSON.stringify(accountability)); }, [accountability]);
  useEffect(() => { localStorage.setItem('financeTransactionColumns', JSON.stringify(visibleTransactionColumns)); }, [visibleTransactionColumns]);

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === selectedAccountId) || null,
    [accounts, selectedAccountId],
  );
  const isOpenFinanceAccount = selectedAccount?.integrationType === 'API';

  const categoriesMap = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category && typeof category.id === 'number') {
        map.set(category.id, category);
      }
    });
    return map;
  }, [categories]);

  const subcategoriesByCategory = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category && typeof category.id === 'number') {
        const list = Array.isArray(category.subcategories)
          ? category.subcategories
          : [];
        map.set(category.id, list);
      }
    });
    return map;
  }, [categories]);

  const manualFormSubcategories = useMemo(() => {
    if (!manualFormState.categoryId) {
      return [];
    }
    const parsed = Number(manualFormState.categoryId);
    if (!Number.isFinite(parsed)) {
      return [];
    }
    return subcategoriesByCategory.get(parsed) || [];
  }, [manualFormState.categoryId, subcategoriesByCategory]);

  const filterSubcategories = useMemo(() => {
    if (!categoryFilter || categoryFilter === 'none') {
      return [];
    }
    const parsed = Number(categoryFilter);
    if (!Number.isFinite(parsed)) {
      return [];
    }
    return subcategoriesByCategory.get(parsed) || [];
  }, [categoryFilter, subcategoriesByCategory]);

  const editingSubcategories = useMemo(() => {
    if (!editingTransactionCategoryId) {
      return [];
    }
    const parsed = Number(editingTransactionCategoryId);
    if (!Number.isFinite(parsed)) {
      return [];
    }
    return subcategoriesByCategory.get(parsed) || [];
  }, [editingTransactionCategoryId, subcategoriesByCategory]);

  const flattenedSubcategories = useMemo(() => {
    const list = [];
    categories.forEach((category) => {
      if (category && typeof category.id === 'number') {
        const subs = Array.isArray(category.subcategories)
          ? category.subcategories
          : [];
        subs.forEach((sub) => {
          if (sub && typeof sub.id === 'number') {
            list.push({
              ...sub,
              categoryId: category.id,
              categoryName: category.name,
            });
          }
        });
      }
    });
    return list;
  }, [categories]);

  const formatChartCurrency = useCallback((rawValue) => {
    const numeric = typeof rawValue === 'number' ? rawValue : Number(rawValue || 0);
    if (!Number.isFinite(numeric)) {
      return formatCurrency(0);
    }
    return formatCurrency(numeric);
  }, []);

  const categoryMovementChart = useMemo(() => {
    if (!categoryReportData || !Array.isArray(categoryReportData.categories)) {
      return null;
    }

    const categoriesWithMovement = categoryReportData.categories
      .map((category) => ({
        ...category,
        movement:
          Number(category?.totalCredits || 0) + Number(category?.totalDebits || 0),
      }))
      .filter((entry) => entry.movement > 0);

    if (categoriesWithMovement.length === 0) {
      return null;
    }

    return {
      labels: categoriesWithMovement.map((entry) => entry.name),
      datasets: [
        {
          label: 'Movimentação total (R$)',
          data: categoriesWithMovement.map((entry) => entry.movement),
          backgroundColor: categoriesWithMovement.map((entry, index) =>
            getColorByIndex(entry.color, index),
          ),
          borderWidth: 1,
        },
      ],
    };
  }, [categoryReportData]);

  const categoryMovementChartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.label}: ${formatChartCurrency(context.parsed)}`,
          },
        },
      },
    }),
    [formatChartCurrency],
  );

  const categoryBalanceChart = useMemo(() => {
    if (!categoryReportData || !Array.isArray(categoryReportData.categories)) {
      return null;
    }

    const labels = categoryReportData.categories.map((category) => category.name);
    if (labels.length === 0) {
      return null;
    }

    const creditData = categoryReportData.categories.map((category) =>
      Number(category?.totalCredits || 0),
    );
    const debitData = categoryReportData.categories.map((category) =>
      Number(category?.totalDebits || 0),
    );

    const hasValues =
      creditData.some((value) => value > 0) || debitData.some((value) => value > 0);
    if (!hasValues) {
      return null;
    }

    return {
      labels,
      datasets: [
        {
          label: 'Receitas (R$)',
          data: creditData,
          backgroundColor: 'rgba(16, 185, 129, 0.6)',
          borderColor: 'rgba(5, 150, 105, 1)',
          borderWidth: 1,
        },
        {
          label: 'Despesas (R$)',
          data: debitData,
          backgroundColor: 'rgba(248, 113, 113, 0.6)',
          borderColor: 'rgba(220, 38, 38, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [categoryReportData]);

  const categoryBalanceChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value =
                typeof context.parsed === 'object'
                  ? context.parsed.y ?? 0
                  : context.parsed;
              return `${context.dataset.label}: ${formatChartCurrency(value)}`;
            },
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: (value) => formatChartCurrency(value),
          },
        },
      },
    }),
    [formatChartCurrency],
  );

  const topSubcategoryChart = useMemo(() => {
    if (!categoryReportData || !Array.isArray(categoryReportData.categories)) {
      return null;
    }

    const entries = [];
    categoryReportData.categories.forEach((category) => {
      const subcategories = Array.isArray(category?.subcategories)
        ? category.subcategories
        : [];
      subcategories.forEach((subcategory) => {
        const totalCredits = Number(subcategory?.totalCredits || 0);
        const totalDebits = Number(subcategory?.totalDebits || 0);
        const totalMovement = totalCredits + totalDebits;
        if (totalMovement <= 0) {
          return;
        }
        entries.push({
          label: `${category.name} • ${subcategory.name}`,
          movement: totalMovement,
          color: subcategory.color ?? category.color ?? null,
        });
      });
    });

    entries.sort((a, b) => b.movement - a.movement);
    const topEntries = entries.slice(0, 10);
    if (topEntries.length === 0) {
      return null;
    }

    return {
      labels: topEntries.map((entry) => entry.label),
      datasets: [
        {
          label: 'Movimentação total (R$)',
          data: topEntries.map((entry) => entry.movement),
          backgroundColor: topEntries.map((entry, index) =>
            getColorByIndex(entry.color, index),
          ),
          borderWidth: 1,
        },
      ],
    };
  }, [categoryReportData]);

  const topSubcategoryChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value =
                typeof context.parsed === 'object'
                  ? context.parsed.y ?? 0
                  : context.parsed;
              return `${formatChartCurrency(value)}`;
            },
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: (value) => formatChartCurrency(value),
          },
        },
      },
    }),
    [formatChartCurrency],
  );

  const categoryReportGeneratedAt = useMemo(() => {
    if (!categoryReportData?.generatedAt) {
      return '';
    }
    const parsed = new Date(categoryReportData.generatedAt);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toLocaleString('pt-BR');
  }, [categoryReportData]);

  const hasCategoryReportData = useMemo(
    () =>
      Array.isArray(categoryReportData?.categories) &&
      categoryReportData.categories.length > 0,
    [categoryReportData],
  );

  const categoryReportTotals = useMemo(() => {
    if (!categoryReportData) {
      return { credits: 0, debits: 0, balance: 0 };
    }
    return {
      credits: Number(categoryReportData.totalCredits || 0),
      debits: Number(categoryReportData.totalDebits || 0),
      balance: Number(categoryReportData.balance || 0),
    };
  }, [categoryReportData]);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    setAccountsError('');
    try {
      const { data } = await FinanceService.listAccounts();
      const list = Array.isArray(data) ? sortAccountsByName(data) : [];
      setAccounts(list);
      setSelectedAccountId((current) => {
        if (current && list.some((account) => account.id === current)) {
          return current;
        }
        return list.length > 0 ? list[0].id : null;
      });
    } catch (error) {
      setAccounts([]);
      setSelectedAccountId(null);
      const message = resolveErrorMessage(error);
      setAccountsError(message);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    setCategoryError('');
    try {
      const { data } = await FinanceService.listCategories();
      const list = Array.isArray(data) ? data : [];
      setCategories(list);
    } catch (error) {
      setCategories([]);
      setCategoryError(resolveErrorMessage(error));
    } finally {
      setCategoriesLoading(false);
    }
  }, []);



  const loadFinanceSettings = useCallback(async () => {
    try {
      const [{ data: settingsData }, { data: companiesData }] = await Promise.all([
        FinanceService.getSettings(),
        FinanceService.listCompanies(),
      ]);
      if (Array.isArray(settingsData?.transactionColumns) && settingsData.transactionColumns.length > 0) {
        setVisibleTransactionColumns(settingsData.transactionColumns);
      }
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
    } catch (error) {
      showAlert('error', resolveErrorMessage(error));
    }
  }, [showAlert]);

  useCustomerRegistryRefresh(kind => {
    if (kind !== 'templates') FinanceService.listCompanies().then(({data}) => setCompanies(Array.isArray(data) ? data : [])).catch(() => {});
  });

  const handleCategoryFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setCategoryFormState((prev) => ({ ...prev, [name]: value }));
    setCategoryError('');
  }, []);

  const handleSubcategoryFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setSubcategoryFormState((prev) => ({ ...prev, [name]: value }));
    setSubcategoryError('');
  }, []);

  const handleCategoryFilterChange = useCallback(
    (event) => {
      const { value } = event.target;
      setCategoryFilter(value);
      if (!value || value === 'none') {
        setSubcategoryFilter('');
        return;
      }
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        setSubcategoryFilter('');
        return;
      }
      const available = subcategoriesByCategory.get(parsed) || [];
      setSubcategoryFilter((previous) => {
        if (!previous || previous === 'none') {
          return previous;
        }
        const parsedPrevious = Number(previous);
        if (!Number.isFinite(parsedPrevious)) {
          return '';
        }
        return available.some((item) => item && item.id === parsedPrevious)
          ? previous
          : '';
      });
    },
    [subcategoriesByCategory],
  );

  const handleSubcategoryFilterChange = useCallback((event) => {
    setSubcategoryFilter(event.target.value);
  }, []);

  const handleCreateCategory = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      const name = categoryFormState.name.trim();
      if (!name) {
        setCategoryError('Informe o nome da categoria.');
        return;
      }

      setCategorySubmitting(true);
      setCategoryError('');
      try {
        await FinanceService.createCategory({
          name,
          color: categoryFormState.color?.trim() || null,
          description: categoryFormState.description?.trim() || null,
        });
        showAlert('success', 'Categoria criada com sucesso.');
        setCategoryFormState({ name: '', color: '', description: '' });
        await loadCategories();
      } catch (error) {
        setCategoryError(resolveErrorMessage(error));
      } finally {
        setCategorySubmitting(false);
      }
    },
    [canManage, categoryFormState, loadCategories, showAlert],
  );

  const handleCreateSubcategory = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      const categoryIdValue = Number(subcategoryFormState.categoryId);
      if (!Number.isFinite(categoryIdValue) || categoryIdValue <= 0) {
        setSubcategoryError('Selecione a categoria.');
        return;
      }
      const name = subcategoryFormState.name.trim();
      if (!name) {
        setSubcategoryError('Informe o nome da subcategoria.');
        return;
      }

      setSubcategorySubmitting(true);
      setSubcategoryError('');
      try {
        await FinanceService.createSubcategory(categoryIdValue, {
          name,
          color: subcategoryFormState.color?.trim() || null,
          description: subcategoryFormState.description?.trim() || null,
        });
        showAlert('success', 'Subcategoria criada com sucesso.');
        setSubcategoryFormState({ categoryId: '', name: '', color: '', description: '' });
        await loadCategories();
      } catch (error) {
        setSubcategoryError(resolveErrorMessage(error));
      } finally {
        setSubcategorySubmitting(false);
      }
    },
    [canManage, loadCategories, showAlert, subcategoryFormState],
  );

  const handleDeleteCategory = useCallback(
    async (id) => {
      if (!canManage) return;
      if (typeof window !== 'undefined') {
        const confirmed = window.confirm('Deseja realmente excluir esta categoria?');
        if (!confirmed) return;
      }
      setCategoryDeletingId(id);
      try {
        await FinanceService.deleteCategory(id);
        showAlert('success', 'Categoria removida com sucesso.');
        setCategoryFilter((prev) => {
          if (!prev || prev === 'none') {
            return prev;
          }
          return Number(prev) === Number(id) ? '' : prev;
        });
        setSubcategoryFilter('');
        await loadCategories();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setCategoryDeletingId(null);
      }
    },
    [canManage, loadCategories, showAlert],
  );

  const handleDeleteSubcategory = useCallback(
    async (id) => {
      if (!canManage) return;
      if (typeof window !== 'undefined') {
        const confirmed = window.confirm('Deseja realmente excluir esta subcategoria?');
        if (!confirmed) return;
      }
      setSubcategoryDeletingId(id);
      try {
        await FinanceService.deleteSubcategory(id);
        showAlert('success', 'Subcategoria removida com sucesso.');
        setSubcategoryFilter((prev) => {
          if (!prev || prev === 'none') {
            return prev;
          }
          return Number(prev) === Number(id) ? '' : prev;
        });
        await loadCategories();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setSubcategoryDeletingId(null);
      }
    },
    [canManage, loadCategories, showAlert],
  );

  const handleEditCategory = useCallback(
    async (category) => {
      if (!canManage || !category || typeof category.id !== 'number') return;
      if (typeof window === 'undefined') return;
      const namePrompt = window.prompt('Nome da categoria', category.name || '');
      if (namePrompt === null) return;
      const normalizedName = namePrompt.trim();
      if (!normalizedName) {
        showAlert('error', 'Informe um nome válido.');
        return;
      }
      const colorPrompt = window.prompt(
        'Cor hexadecimal (opcional, ex: #FFAA00)',
        category.color || '',
      );
      if (colorPrompt === null) return;
      const descriptionPrompt = window.prompt(
        'Descrição (opcional)',
        category.description || '',
      );
      if (descriptionPrompt === null) return;

      setCategoryUpdatingId(category.id);
      try {
        await FinanceService.updateCategory(category.id, {
          name: normalizedName,
          color: colorPrompt?.trim() || null,
          description: descriptionPrompt?.trim() || null,
        });
        showAlert('success', 'Categoria atualizada com sucesso.');
        await loadCategories();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setCategoryUpdatingId(null);
      }
    },
    [canManage, loadCategories, showAlert],
  );

  const handleEditSubcategory = useCallback(
    async (subcategory) => {
      if (!canManage || !subcategory || typeof subcategory.id !== 'number') return;
      if (typeof window === 'undefined') return;
      const namePrompt = window.prompt('Nome da subcategoria', subcategory.name || '');
      if (namePrompt === null) return;
      const normalizedName = namePrompt.trim();
      if (!normalizedName) {
        showAlert('error', 'Informe um nome válido.');
        return;
      }
      const colorPrompt = window.prompt(
        'Cor hexadecimal (opcional, ex: #FFAA00)',
        subcategory.color || '',
      );
      if (colorPrompt === null) return;
      const descriptionPrompt = window.prompt(
        'Descrição (opcional)',
        subcategory.description || '',
      );
      if (descriptionPrompt === null) return;

      setSubcategoryUpdatingId(subcategory.id);
      try {
        await FinanceService.updateSubcategory(subcategory.id, {
          name: normalizedName,
          color: colorPrompt?.trim() || null,
          description: descriptionPrompt?.trim() || null,
          categoryId: subcategory.categoryId,
        });
        showAlert('success', 'Subcategoria atualizada com sucesso.');
        await loadCategories();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setSubcategoryUpdatingId(null);
      }
    },
    [canManage, loadCategories, showAlert],
  );

  const handleOpenCategoryManager = useCallback(() => {
    setShowCategoryManager(true);
  }, []);

  const handleCloseCategoryManager = useCallback(() => {
    setShowCategoryManager(false);
    setCategoryError('');
    setSubcategoryError('');
  }, []);

  const loadCategoryReport = useCallback(async (scope = 'selected') => {
    const accountIds = scope === 'all'
      ? accounts.map((account) => account.id).filter(Boolean)
      : [selectedAccountId].filter(Boolean);

    if (accountIds.length === 0) {
      setCategoryReportData(null);
      return;
    }
    setCategoryReportLoading(true);
    setCategoryReportError('');
    try {
      const params = {};
      const fromParam = buildDateParam(fromDate, false);
      const toParam = buildDateParam(toDate, true);
      if (fromParam) params.from = fromParam;
      if (toParam) params.to = toParam;
      if (categoryFilter) {
        params.categoryId = categoryFilter === 'none' ? 'none' : categoryFilter;
      }
      if (subcategoryFilter) {
        params.subcategoryId =
          subcategoryFilter === 'none' ? 'none' : subcategoryFilter;
      }
      const reports = await Promise.all(
        accountIds.map((accountId) =>
          FinanceService.getCategorySummary(accountId, params).then(({ data }) => data),
        ),
      );
      setCategoryReportData(scope === 'all' ? mergeCategoryReports(reports) : (reports[0] || null));
    } catch (error) {
      setCategoryReportError(resolveErrorMessage(error));
      setCategoryReportData(null);
    } finally {
      setCategoryReportLoading(false);
    }
  }, [
    accounts,
    categoryFilter,
    fromDate,
    selectedAccountId,
    subcategoryFilter,
    toDate,
  ]);

  useEffect(() => {
    if (activeFinanceTab === 'categorySummary') {
      loadCategoryReport('all');
    }
  }, [activeFinanceTab, loadCategoryReport]);

  const handleOpenCategoryReport = useCallback(async () => {
    setShowCategoryReport(true);
    await loadCategoryReport('selected');
  }, [loadCategoryReport]);

  const handleCloseCategoryReport = useCallback(() => {
    setShowCategoryReport(false);
    setCategoryReportData(null);
    setCategoryReportError('');
  }, []);

  const loadTransactions = useCallback(async () => {
    if (!selectedAccountId) {
      setTransactions([]);
      setTransactionSummary(null);
      setTransactionsError('');
      setEditingTransactionId(null);
      setEditingTransactionDescription('');
      setEditingTransactionError('');
      setUpdatingTransaction(false);
      return;
    }

    setTransactionsLoading(true);
    setTransactionsError('');
    setDeletingTransactionIds([]);
    setEditingTransactionId(null);
    setEditingTransactionDescription('');
    setEditingTransactionError('');
    setUpdatingTransaction(false);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const fromParam = buildDateParam(fromDate, false);
      const toParam = buildDateParam(toDate, true);
      if (fromParam) {
        params.from = fromParam;
      }
      if (toParam) {
        params.to = toParam;
      }
      if (categoryFilter) {
        params.categoryId = categoryFilter === 'none' ? 'none' : categoryFilter;
      }
      if (subcategoryFilter) {
        params.subcategoryId =
          subcategoryFilter === 'none' ? 'none' : subcategoryFilter;
      }
      params.page = transactionPage;
      params.pageSize = 25;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const { data } = await FinanceService.listTransactions(selectedAccountId, params);
      const fetchedTransactions = Array.isArray(data?.transactions) ? data.transactions : [];
      setTransactions(fetchedTransactions);
      setTransactionSummary(data?.summary || null);
      setTransactionPagination(data?.pagination || { page: 1, pageSize: 25, total: fetchedTransactions.length, totalPages: 1 });
      setSelectedTransactionIds([]);
    } catch (error) {
      const message = resolveErrorMessage(error);
      setTransactionsError(message);
      setTransactions([]);
      setTransactionSummary(null);
    } finally {
      setTransactionsLoading(false);
    }
  }, [
    categoryFilter,
    fromDate,
    searchTerm,
    selectedAccountId,
    statusFilter,
    subcategoryFilter,
    toDate,
    transactionPage,
  ]);

  useEffect(() => {
    if (!canView) {
      setAccounts([]);
      setSelectedAccountId(null);
      return;
    }
    loadAccounts();
  }, [canView, loadAccounts]);

  useEffect(() => {
    if (!canView) {
      setCategories([]);
      return;
    }
    loadCategories();
  }, [canView, loadCategories]);

  useEffect(() => {
    if (!canView) {
      setCompanies([]);
      return;
    }
    loadFinanceSettings();
  }, [canView, loadFinanceSettings]);

  useEffect(() => {
    if (!selectedAccountId) {
      setTransactions([]);
      setTransactionSummary(null);
      return;
    }
    loadTransactions();
  }, [loadTransactions, selectedAccountId]);

  useEffect(() => {
    setShowManualTransactionForm(false);
    setManualFormState({
      ...emptyManualTransactionForm,
      occurredAt: '',
    });
    setManualFormError('');
    setEditingTransactionId(null);
    setEditingTransactionDescription('');
    setEditingTransactionError('');
    setUpdatingTransaction(false);
    setCategoryFilter('');
    setSubcategoryFilter('');
  }, [selectedAccountId]);

  useEffect(() => {
    setManualFormState((prev) => {
      if (!prev.categoryId) {
        return prev;
      }
      const parsedCategory = Number(prev.categoryId);
      if (!Number.isFinite(parsedCategory) || !categoriesMap.has(parsedCategory)) {
        return {
          ...prev,
          categoryId: '',
          subcategoryId: '',
        };
      }
      if (!prev.subcategoryId) {
        return prev;
      }
      const parsedSubcategory = Number(prev.subcategoryId);
      const subcategories = subcategoriesByCategory.get(parsedCategory) || [];
      if (
        !Number.isFinite(parsedSubcategory) ||
        !subcategories.some((item) => item && item.id === parsedSubcategory)
      ) {
        return {
          ...prev,
          subcategoryId: '',
        };
      }
      return prev;
    });
  }, [categoriesMap, subcategoriesByCategory]);

  useEffect(() => {
    if (editingTransactionId === null) {
      return;
    }
    setEditingTransactionCategoryId((prev) => {
      if (!prev) {
        return prev;
      }
      const parsed = Number(prev);
      if (!Number.isFinite(parsed) || !categoriesMap.has(parsed)) {
        setEditingTransactionSubcategoryId('');
    setEditingTransactionDocumentNumber('');
        return '';
      }
      return prev;
    });
    setEditingTransactionSubcategoryId((prev) => {
      if (!editingTransactionCategoryId) {
        return '';
      }
      if (!prev) {
        return '';
      }
      const parsedCategory = Number(editingTransactionCategoryId);
      const parsedSubcategory = Number(prev);
      if (!Number.isFinite(parsedCategory) || !Number.isFinite(parsedSubcategory)) {
        return '';
      }
      const subcategories = subcategoriesByCategory.get(parsedCategory) || [];
      return subcategories.some((item) => item && item.id === parsedSubcategory)
        ? prev
        : '';
    });
  }, [categoriesMap, editingTransactionCategoryId, editingTransactionId, subcategoriesByCategory]);

  useEffect(() => {
    if (!alert) return undefined;
    const timer = setTimeout(() => setAlert(null), 6000);
    return () => clearTimeout(timer);
  }, [alert]);

  const integrationLabel = useMemo(() => {
    if (!selectedAccount) return '';
    const option = INTEGRATION_TYPES.find((item) => item.value === selectedAccount.integrationType);
    return option ? option.label : 'Integração personalizada';
  }, [selectedAccount]);

  const displayedTransactions = useMemo(() => {
    return transactions;
  }, [transactions]);

  useEffect(() => {
    setTransactionPage(1);
  }, [selectedAccountId, statusFilter, fromDate, toDate, categoryFilter, subcategoryFilter, searchTerm]);

  const selectedAccountabilityTransaction = useMemo(() => {
    if (!displayedTransactions.length) return null;
    return displayedTransactions.find((transaction) => transaction.id === selectedAccountabilityTransactionId) || displayedTransactions[0];
  }, [displayedTransactions, selectedAccountabilityTransactionId]);

  const selectedAccountabilityData = selectedAccountabilityTransaction
    ? (accountability[selectedAccountabilityTransaction.id] || {})
    : {};

  const selectedAccountabilityCompany = selectedAccountabilityData.companyId
    ? companies.find((company) => String(company.id) === String(selectedAccountabilityData.companyId))
    : null;

  const selectedAccountabilityDocumentType = selectedAccountabilityData.documentType || 'NFE';

  const selectedIdsSet = useMemo(
    () => new Set(selectedTransactionIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))),
    [selectedTransactionIds],
  );

  const deletingIdsSet = useMemo(
    () => new Set(deletingTransactionIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))),
    [deletingTransactionIds],
  );

  const selectionStats = useMemo(() => {
    if (!selectedIdsSet.size) {
      return {
        count: 0,
        total: 0,
        credits: 0,
        debits: 0,
      };
    }
    let total = 0;
    let credits = 0;
    let debits = 0;
    for (const transaction of transactions) {
      if (!selectedIdsSet.has(transaction.id)) continue;
      total += Number(transaction.amount || 0);
      if (Number(transaction.amount || 0) >= 0) {
        credits += 1;
      } else {
        debits += 1;
      }
    }
    return {
      count: selectedIdsSet.size,
      total,
      credits,
      debits,
    };
  }, [selectedIdsSet, transactions]);

  const summary = useMemo(
    () => ({
      totalCredits: transactionSummary?.totalCredits || 0,
      totalDebits: transactionSummary?.totalDebits || 0,
      balance: transactionSummary?.balance || 0,
      reconciledCount: transactionSummary?.reconciledCount || 0,
      pendingCount: transactionSummary?.pendingCount || 0,
      divergentCount: transactionSummary?.divergentCount || 0,
    }),
    [transactionSummary],
  );

  const handleEditAccount = useCallback(
    (account) => {
      if (!canManage) return;
      setFormState(toFormState(account));
      setFormError('');
      setShowForm(true);
    },
    [canManage],
  );

  const handleCreateAccount = useCallback(() => {
    if (!canManage) return;
    setFormState({ ...emptyForm });
    setFormError('');
    setShowForm(true);
  }, [canManage]);

  const handleCancelForm = useCallback(() => {
    setShowForm(false);
    setFormState({ ...emptyForm });
    setFormError('');
  }, []);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSaveAccount = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;

      if (!formState.name.trim() || !formState.bankName.trim() || !formState.accountNumber.trim()) {
        setFormError('Informe nome, banco e número da conta.');
        return;
      }

      const description = formState.description.trim();

      const payload = {
        name: formState.name.trim(),
        bankName: formState.bankName.trim(),
        agencyNumber: formState.agencyNumber.trim(),
        accountNumber: formState.accountNumber.trim(),
        description: description || undefined,
        integrationType: formState.integrationType,
        integrationSyncStartAt: formState.integrationType === 'API'
          ? formState.integrationSyncStartAt
          : null,
      };

      setSavingAccount(true);
      setFormError('');
      try {
        let savedAccount;
        if (formState.id) {
          const { data } = await FinanceService.updateAccount(formState.id, payload);
          savedAccount = data;
          setAccounts((prev) => sortAccountsByName(prev.map((account) => (account.id === data.id ? data : account))));
          setSelectedAccountId(data.id);
          showAlert('success', 'Conta bancária atualizada com sucesso.');
        } else {
          const { data } = await FinanceService.createAccount(payload);
          savedAccount = data;
          setAccounts((prev) => sortAccountsByName([...prev, data]));
          setSelectedAccountId(data.id);
          showAlert('success', 'Conta bancária criada com sucesso.');
        }
        if (
          savedAccount?.integrationType === 'API' &&
          formState.originalIntegrationType !== 'API'
        ) {
          setActiveFinanceTab('transactions');
          setOpenFinanceAutoStartAccountId(savedAccount.id);
          showAlert('success', 'Conta salva. Escolha Open Finance ou credenciais para conectar o banco.');
        }
        setShowForm(false);
        setFormState({ ...emptyForm });
      } catch (error) {
        setFormError(resolveErrorMessage(error));
      } finally {
        setSavingAccount(false);
      }
    },
    [canManage, formState, showAlert],
  );

  const handleDeleteAccount = useCallback(
    async (accountId) => {
      if (!canManage) return;
      const account = accounts.find((item) => item.id === accountId);
      const confirmationMessage = account
        ? `Deseja realmente remover a conta "${account.name}"?`
        : 'Deseja realmente remover esta conta bancária?';
      if (typeof window !== 'undefined' && !window.confirm(confirmationMessage)) {
        return;
      }
      setDeletingAccountId(accountId);
      try {
        await FinanceService.deleteAccount(accountId);
        setAccounts((prev) => {
          const next = sortAccountsByName(prev.filter((item) => item.id !== accountId));
          setSelectedAccountId((current) => {
            if (!current || !next.some((item) => item.id === current) || current === accountId) {
              return next.length > 0 ? next[0].id : null;
            }
            return current;
          });
          return next;
        });
        setSelectedTransactionIds([]);
        showAlert('success', 'Conta bancária removida com sucesso.');
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setDeletingAccountId(null);
      }
    },
    [accounts, canManage, showAlert],
  );

  const handleSelectAccount = useCallback((accountId) => {
    setSelectedAccountId(accountId);
    setShowForm(false);
    setFormState({ ...emptyForm });
    setSelectedTransactionIds([]);
    setTransactionsError('');
    setAlert(null);
  }, []);

  const handleToggleTransactionSelection = useCallback((transactionId) => {
    setSelectedTransactionIds((prev) => {
      if (prev.includes(transactionId)) {
        return prev.filter((id) => id !== transactionId);
      }
      return [...prev, transactionId];
    });
  }, []);

  const handleSelectAllDisplayed = useCallback(() => {
    const displayedIds = displayedTransactions.map((transaction) => transaction.id);
    const allSelected = displayedIds.every((id) => selectedIdsSet.has(id));
    if (allSelected) {
      setSelectedTransactionIds((prev) => prev.filter((id) => !displayedIds.includes(id)));
    } else {
      const next = new Set(selectedTransactionIds);
      displayedIds.forEach((id) => next.add(id));
      setSelectedTransactionIds(Array.from(next));
    }
  }, [displayedTransactions, selectedIdsSet, selectedTransactionIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedTransactionIds([]);
  }, []);

  const triggerFileDialog = useCallback(() => {
    if (!canManage) return;
    if (isOpenFinanceAccount) {
      showAlert('error', 'Esta conta aceita lançamentos somente pela sincronização bancária automática.');
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, [canManage, isOpenFinanceAccount, showAlert]);

  const handleOfxFileChange = useCallback(
    async (event) => {
      if (!canManage) return;
      if (!selectedAccountId) return;
      if (isOpenFinanceAccount) {
        showAlert('error', 'Importações manuais estão bloqueadas nesta conta integrada.');
        event.target.value = '';
        return;
      }
      const file = event.target.files?.[0];
      if (!file) return;
      setImportingOfx(true);
      try {
        const lowerName = file.name.toLowerCase();
        if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
          const buffer = await file.arrayBuffer();
          const workbook = XLSX.read(buffer, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          let imported = 0;
          for (const row of rows) {
            const amount = parseAmountInput(row.Valor ?? row.valor ?? row.Amount ?? row.amount);
            if (!Number.isFinite(amount) || amount === 0) continue;
            const rawType = String(row.Tipo ?? row.tipo ?? '').toUpperCase();
            await FinanceService.createManualTransaction(selectedAccountId, {
              type: rawType.includes('CREDIT') || amount > 0 ? 'CREDIT' : 'DEBIT',
              amount: Math.abs(amount),
              occurredAt: row.Data ? new Date(row.Data).toISOString() : undefined,
              description: row.Descrição || row.Descricao || row.description || undefined,
              documentNumber: row['Nº documento'] || row.Documento || row.documentNumber || undefined,
              notes: row.Observações || row.Observacoes || undefined,
            });
            imported += 1;
          }
          showAlert('success', `${imported} transações importadas do Excel.`);
        } else {
          const { data } = await FinanceService.importOfx(selectedAccountId, file);
          const imported = Number(data?.importedCount || 0);
          const skipped = Number(data?.skippedCount || 0);
          const messageParts = [];
          if (imported > 0) messageParts.push(`${imported} transações importadas`);
          if (skipped > 0) messageParts.push(`${skipped} duplicadas ignoradas`);
          showAlert('success', messageParts.length ? messageParts.join(' · ') : 'Importação concluída.');
        }
        await loadTransactions();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setImportingOfx(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [canManage, isOpenFinanceAccount, loadTransactions, selectedAccountId, showAlert],
  );

  const handleOpenManualForm = useCallback(() => {
    if (!canManage) return;
    if (isOpenFinanceAccount) {
      showAlert('error', 'Lançamentos manuais estão bloqueados nesta conta integrada.');
      return;
    }
    setShowManualTransactionForm(true);
    setManualFormError('');
    setManualFormState((prev) => ({
      ...prev,
      occurredAt: prev.occurredAt || '',
    }));
  }, [canManage, isOpenFinanceAccount, showAlert]);

  const handleCloseManualForm = useCallback(() => {
    setShowManualTransactionForm(false);
    setManualFormError('');
    setManualFormState({
      ...emptyManualTransactionForm,
      occurredAt: '',
    });
  }, []);

  const handleManualFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setManualFormState((prev) => {
      if (name === 'categoryId') {
        return {
          ...prev,
          categoryId: value,
          subcategoryId: '',
        };
      }
      if (name === 'subcategoryId') {
        return { ...prev, subcategoryId: value };
      }
      if (name === 'amount') {
        return { ...prev, amount: formatCurrencyInput(value) };
      }
      return { ...prev, [name]: value };
    });
    setManualFormError('');
  }, []);

  const handleManualFormSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage || !selectedAccountId) return;
      if (isOpenFinanceAccount) {
        setManualFormError('Lançamentos manuais estão bloqueados nesta conta integrada.');
        return;
      }

      const type = manualFormState.type === 'CREDIT' ? 'CREDIT' : 'DEBIT';
      const amountNumber = parseAmountInput(manualFormState.amount);
      if (!Number.isFinite(amountNumber) || Math.abs(amountNumber) === 0) {
        setManualFormError('Informe um valor válido para a transação.');
        return;
      }

      const occurredAtDate = manualFormState.occurredAt ? new Date(manualFormState.occurredAt) : new Date();
      if (manualFormState.occurredAt && Number.isNaN(occurredAtDate.getTime())) {
        setManualFormError('Informe uma data válida para a transação.');
        return;
      }

      const description = manualFormState.description
        ? manualFormState.description.trim()
        : '';
      const notes = manualFormState.notes ? manualFormState.notes.trim() : '';
      const documentNumber = manualFormState.documentNumber ? manualFormState.documentNumber.trim() : '';

      let categoryIdValue = null;
      if (manualFormState.categoryId) {
        const parsed = Number(manualFormState.categoryId);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          setManualFormError('Selecione uma categoria válida.');
          return;
        }
        categoryIdValue = parsed;
      }

      let subcategoryIdValue = null;
      if (manualFormState.subcategoryId) {
        const parsed = Number(manualFormState.subcategoryId);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          setManualFormError('Selecione uma subcategoria válida.');
          return;
        }
        subcategoryIdValue = parsed;
      }

      setCreatingManualTransaction(true);
      setManualFormError('');
      try {
        const payload = {
          type,
          amount: amountNumber,
          occurredAt: manualFormState.occurredAt ? occurredAtDate.toISOString() : undefined,
          description: description || undefined,
          notes: notes || undefined,
          documentNumber: documentNumber || undefined,
          categoryId: categoryIdValue,
          subcategoryId: subcategoryIdValue,
        };
        const { data } = await FinanceService.createManualTransaction(
          selectedAccountId,
          payload,
        );
        if (data?.summary) {
          setTransactionSummary(data.summary);
        }
        await loadTransactions();
        showAlert('success', 'Transação manual adicionada com sucesso.');
        setShowManualTransactionForm(false);
        setManualFormState({
          ...emptyManualTransactionForm,
          occurredAt: '',
        });
      } catch (error) {
        setManualFormError(resolveErrorMessage(error));
      } finally {
        setCreatingManualTransaction(false);
      }
    },
    [
      canManage,
      isOpenFinanceAccount,
      loadTransactions,
      manualFormState,
      selectedAccountId,
      showAlert,
    ],
  );

  const handleDeleteTransaction = useCallback(
    async (transactionId) => {
      if (!canManage || !selectedAccountId) return;
      if (isOpenFinanceAccount) {
        showAlert('error', 'Transações sincronizadas não podem ser excluídas manualmente.');
        return;
      }

      const transaction = transactions.find((item) => item.id === transactionId);
      if (!transaction) {
        showAlert('error', 'Transação não encontrada.');
        return;
      }
      if (transaction.status === 'RECONCILED') {
        showAlert('error', 'Altere o status da transação conciliada antes de excluir.');
        return;
      }
      const confirmationMessage = transaction?.description
        ? `Deseja realmente excluir a transação "${transaction.description}"?`
        : 'Deseja realmente excluir esta transação?';
      if (typeof window !== 'undefined' && !window.confirm(confirmationMessage)) {
        return;
      }

      setDeletingTransactionIds((prev) => [...prev, transactionId]);
      try {
        const { data } = await FinanceService.deleteTransaction(
          selectedAccountId,
          transactionId,
        );
        setTransactions((prev) => prev.filter((item) => item.id !== transactionId));
        setSelectedTransactionIds((prev) => prev.filter((id) => id !== transactionId));
        if (data?.summary) {
          setTransactionSummary(data.summary);
        }
        showAlert('success', 'Transação excluída com sucesso.');
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setDeletingTransactionIds((prev) => prev.filter((id) => id !== transactionId));
      }
    },
    [canManage, isOpenFinanceAccount, selectedAccountId, showAlert, transactions],
  );

  const handleDeleteSelectedTransactions = useCallback(async () => {
    if (!canManage || !selectedAccountId || selectedTransactionIds.length === 0) return;
    if (isOpenFinanceAccount) {
      showAlert('error', 'Transações sincronizadas não podem ser excluídas manualmente.');
      return;
    }
    const selectedSet = new Set(selectedTransactionIds.map(Number));
    const selected = transactions.filter((transaction) => selectedSet.has(transaction.id));
    const blocked = selected.filter((transaction) => transaction.status === 'RECONCILED');
    if (blocked.length > 0) {
      showAlert('error', 'Altere o status das transações conciliadas antes de excluir em lote.');
      return;
    }
    if (selected.length === 0) return;
    if (typeof window !== 'undefined' && !window.confirm(`Deseja realmente excluir ${selected.length} transações selecionadas?`)) {
      return;
    }
    setDeletingTransactionIds((prev) => [...new Set([...prev, ...selected.map((transaction) => transaction.id)])]);
    try {
      await Promise.all(selected.map((transaction) => FinanceService.deleteTransaction(selectedAccountId, transaction.id)));
      setTransactions((prev) => prev.filter((transaction) => !selectedSet.has(transaction.id)));
      setSelectedTransactionIds([]);
      await loadTransactions();
      showAlert('success', `${selected.length} transações excluídas com sucesso.`);
    } catch (error) {
      showAlert('error', resolveErrorMessage(error));
    } finally {
      setDeletingTransactionIds((prev) => prev.filter((id) => !selectedSet.has(id)));
    }
  }, [canManage, isOpenFinanceAccount, loadTransactions, selectedAccountId, selectedTransactionIds, showAlert, transactions]);

  const handleCancelEditingTransaction = useCallback(() => {
    setEditingTransactionId(null);
    setEditingTransactionDescription('');
    setEditingTransactionError('');
    setUpdatingTransaction(false);
    setEditingTransactionCategoryId('');
    setEditingTransactionSubcategoryId('');
  }, []);

  const handleStartEditingTransaction = useCallback(
    (transaction) => {
      if (!canManage || updatingTransaction) {
        return;
      }
      setEditingTransactionId(transaction.id);
      setEditingTransactionDescription(transaction.description || '');
      setEditingTransactionError('');
      setEditingTransactionDocumentNumber(transaction.documentNumber || '');
      setEditingTransactionCategoryId(
        transaction.category && typeof transaction.category.id === 'number'
          ? String(transaction.category.id)
          : '',
      );
      setEditingTransactionSubcategoryId(
        transaction.subcategory && typeof transaction.subcategory.id === 'number'
          ? String(transaction.subcategory.id)
          : '',
      );
    },
    [canManage, updatingTransaction],
  );

  const handleEditingTransactionCategoryChange = useCallback((event) => {
    const { value } = event.target;
    setEditingTransactionCategoryId(value);
    setEditingTransactionSubcategoryId('');
  }, []);

  const handleEditingTransactionSubcategoryChange = useCallback((event) => {
    setEditingTransactionSubcategoryId(event.target.value);
  }, []);

  const handleUpdateTransaction = useCallback(async () => {
    if (!canManage || !selectedAccountId || editingTransactionId === null) {
      return;
    }
    setUpdatingTransaction(true);
    setEditingTransactionError('');
    try {
      const updatePayload = isOpenFinanceAccount
        ? { description: editingTransactionDescription }
        : {
            description: editingTransactionDescription,
            documentNumber: editingTransactionDocumentNumber,
            categoryId: editingTransactionCategoryId
              ? Number(editingTransactionCategoryId)
              : null,
            subcategoryId: editingTransactionSubcategoryId
              ? Number(editingTransactionSubcategoryId)
              : null,
          };
      const { data } = await FinanceService.updateTransaction(
        selectedAccountId,
        editingTransactionId,
        updatePayload,
      );
      const updatedTransaction = data?.transaction || null;
      setTransactions((prev) =>
        prev.map((item) => {
          if (item.id !== editingTransactionId) {
            return item;
          }
          if (updatedTransaction) {
            return {
              ...item,
              description: updatedTransaction.description ?? null,
              category: updatedTransaction.category ?? null,
              subcategory: updatedTransaction.subcategory ?? null,
              documentNumber: updatedTransaction.documentNumber ?? null,
            };
          }
          const trimmed = editingTransactionDescription.trim();
          return {
            ...item,
            description: trimmed ? trimmed : null,
            documentNumber: editingTransactionDocumentNumber.trim() || null,
            category: editingTransactionCategoryId
              ? categoriesMap.get(Number(editingTransactionCategoryId)) || null
              : null,
            subcategory: editingTransactionSubcategoryId
              ? (editingSubcategories.find(
                  (sub) => sub && Number(sub.id) === Number(editingTransactionSubcategoryId),
                ) || null)
              : null,
          };
        }),
      );
      showAlert('success', 'Transação atualizada com sucesso.');
      handleCancelEditingTransaction();
    } catch (error) {
      setEditingTransactionError(resolveErrorMessage(error));
    } finally {
      setUpdatingTransaction(false);
    }
  }, [
    canManage,
    categoriesMap,
    editingTransactionDescription,
    editingTransactionId,
    editingTransactionCategoryId,
    editingTransactionDocumentNumber,
    editingTransactionSubcategoryId,
    editingSubcategories,
    handleCancelEditingTransaction,
    isOpenFinanceAccount,
    selectedAccountId,
    showAlert,
  ]);

  const handleSyncAccount = useCallback(
    async () => {
      if (!canManage || !selectedAccountId) return;
      setSyncingAccount(true);
      try {
        await FinanceService.syncAccount(selectedAccountId);
        showAlert('success', 'Sincronização solicitada com sucesso.');
        await loadTransactions();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setSyncingAccount(false);
      }
    },
    [canManage, loadTransactions, selectedAccountId, showAlert],
  );

  const handleReconcile = useCallback(
    async () => {
      if (!canManage || !selectedAccountId || selectedTransactionIds.length === 0) {
        return;
      }
      setReconciling(true);
      try {
        const payload = {
          matchedTransactionIds: selectedTransactionIds,
          markOthersAsDivergent,
          notes: reconciliationNotes?.trim() ? reconciliationNotes.trim() : null,
        };
        const { data } = await FinanceService.reconcile(selectedAccountId, payload);
        const matched = Number(data?.matchedCount || selectedTransactionIds.length);
        const divergent = Number(data?.divergentCount || 0);
        const parts = [`${matched} transações conciliadas`];
        if (markOthersAsDivergent && divergent > 0) {
          parts.push(`${divergent} marcadas como divergentes`);
        }
        showAlert('success', parts.join(' · '));
        setReconciliationNotes('');
        setMarkOthersAsDivergent(false);
        setSelectedTransactionIds([]);
        await loadTransactions();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setReconciling(false);
      }
    },
    [canManage, loadTransactions, markOthersAsDivergent, reconciliationNotes, selectedAccountId, selectedTransactionIds, showAlert],
  );



  const handleDownloadImportTemplate = useCallback(() => {
    downloadWorkbook(buildImportTemplateWorkbook(), 'template-importacao-transacoes.xlsx');
  }, []);

  const handleExportCashControl = useCallback(() => {
    const wb = XLSX.utils.book_new();
    const darkGreen = '064E3B';
    const emerald = '10B981';
    const rose = 'F43F5E';
    const softGreen = 'D1FAE5';
    const softRose = 'FFE4E6';
    const softAmber = 'FEF3C7';
    const moneyMask = 'R$ #,##0.00;[Red]-R$ #,##0.00';
    const titleStyle = { fill: { fgColor: { rgb: darkGreen } }, font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 18 }, alignment: { horizontal: 'center' } };
    const sectionStyle = { fill: { fgColor: { rgb: darkGreen } }, font: { color: { rgb: 'FFFFFF' }, bold: true }, alignment: { horizontal: 'center' } };
    const cardValueStyle = (rgb) => ({ fill: { fgColor: { rgb } }, font: { bold: true, sz: 14, color: { rgb: '0F172A' } }, alignment: { horizontal: 'center' }, numFmt: moneyMask });

    const categoryMetrics = categories.map((category, index) => {
      const txs = displayedTransactions.filter((tx) => tx.category?.id === category.id);
      const credits = txs.filter((tx) => tx.amount > 0).reduce((sum, tx) => sum + tx.amount, 0);
      const debits = txs.filter((tx) => tx.amount < 0).reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
      return {
        name: category.name,
        color: getColorByIndex(category.color, index).replace('#', '').toUpperCase(),
        credits,
        debits,
        balance: credits - debits,
        count: txs.length,
        movement: credits + debits,
        description: category.description || '',
      };
    }).filter((category) => category.count > 0 || category.description);
    const topCategories = [...categoryMetrics].sort((a, b) => b.movement - a.movement).slice(0, 8);
    const dashboard = [
      ['CONTROLE DE CAIXA'],
      [`Conta: ${selectedAccount?.name || 'Todas as contas'}`],
      [`Gerado em: ${new Date().toLocaleString('pt-BR')}`],
      [],
      ['Receitas', 'Despesas', 'Saldo', 'Pendentes', 'Conciliadas', 'Divergentes'],
      [summary.totalCredits, summary.totalDebits, summary.balance, summary.pendingCount, summary.reconciledCount, summary.divergentCount],
      [],
      ['INDICADORES OPERACIONAIS'],
      ['Indicador', 'Valor', 'Leitura rápida'],
      ['Fluxo líquido', summary.balance, summary.balance >= 0 ? 'Saldo positivo' : 'Saldo negativo'],
      ['Total de entradas', summary.totalCredits, 'Receitas identificadas'],
      ['Total de saídas', summary.totalDebits, 'Despesas identificadas'],
      ['Itens pendentes', summary.pendingCount, 'Acompanhar na prestação de contas'],
      [],
      ['MOVIMENTAÇÃO POR CATEGORIA'],
      ['Categoria', 'Movimentação', 'Receitas', 'Despesas', 'Saldo', 'Qtd. lançamentos'],
      ...topCategories.map((category) => [category.name, category.movement, category.credits, category.debits, category.balance, category.count]),
      [],
      ['COMPOSIÇÃO DO STATUS'],
      ['Status', 'Qtd.', 'Leitura'],
      ['Pendentes', summary.pendingCount, 'Acompanhar na prestação de contas'],
      ['Conciliadas', summary.reconciledCount, 'Itens fechados'],
      ['Divergentes', summary.divergentCount, 'Itens com ajuste necessário'],
    ];
    const wsDash = XLSX.utils.aoa_to_sheet(dashboard);
    wsDash['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
    wsDash['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 18 }, { wch: 34 }, { wch: 18 }, { wch: 18 }];
    const styleCells = (cells, style) => cells.forEach((cell) => { if (wsDash[cell]) wsDash[cell].s = style; });
    const styleRow = (row, columns, style) => columns.forEach((col) => { if (wsDash[`${col}${row}`]) wsDash[`${col}${row}`].s = style; });
    if (wsDash.A1) wsDash.A1.s = titleStyle;
    styleRow(5, ['A', 'B', 'C', 'D', 'E', 'F'], sectionStyle);
    ['A8', 'A15'].forEach((cell) => { if (wsDash[cell]) wsDash[cell].s = sectionStyle; });
    styleRow(16, ['A', 'B', 'C', 'D', 'E', 'F'], sectionStyle);
    ['A6', 'B6', 'C6'].forEach((cell, index) => { if (wsDash[cell]) wsDash[cell].s = cardValueStyle(index === 1 ? softRose : softGreen); });
    ['D6', 'E6', 'F6'].forEach((cell, index) => { if (wsDash[cell]) wsDash[cell].s = cardValueStyle(index === 0 ? softAmber : softGreen); });
    styleCells(['B10', 'B11', 'B12'], { numFmt: moneyMask });
    const categorySectionRow = 15;
    const categoryHeaderRow = categorySectionRow + 1;
    styleCells([`A${categorySectionRow}`], sectionStyle);
    styleRow(categoryHeaderRow, ['A', 'B', 'C', 'D', 'E', 'F'], sectionStyle);
    for (let row = categoryHeaderRow + 1; row <= categoryHeaderRow + topCategories.length; row += 1) {
      styleCells([`B${row}`, `C${row}`, `D${row}`, `E${row}`], { numFmt: moneyMask });
    }
    const statusSectionRow = categoryHeaderRow + topCategories.length + 2;
    const statusHeaderRow = statusSectionRow + 1;
    styleCells([`A${statusSectionRow}`], sectionStyle);
    styleRow(statusHeaderRow, ['A', 'B', 'C'], sectionStyle);
    XLSX.utils.book_append_sheet(wb, wsDash, 'Dashboard operacional');

    const rows = displayedTransactions.map((tx) => ({
      Data: formatDateTime(tx.occurredAt),
      Descrição: tx.description || '',
      'Nº documento': tx.documentNumber || '',
      Valor: Number(tx.amount || 0),
      Tipo: TRANSACTION_TYPE_LABELS[tx.type] || tx.type,
      Origem: SOURCE_LABELS[tx.source] || tx.source || '',
      Categoria: tx.category?.name || '',
      Subcategoria: tx.subcategory?.name || '',
    }));
    const wsRows = XLSX.utils.json_to_sheet(rows);
    wsRows['!cols'] = [{ wch: 18 }, { wch: 42 }, { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 22 }, { wch: 22 }];
    const range = XLSX.utils.decode_range(wsRows['!ref'] || 'A1:H1');
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cell = wsRows[XLSX.utils.encode_cell({ r: 0, c: col })];
      if (cell) cell.s = sectionStyle;
    }
    for (let row = 1; row <= range.e.r; row += 1) {
      const amountCell = wsRows[XLSX.utils.encode_cell({ r: row, c: 3 })];
      if (amountCell) amountCell.s = { numFmt: moneyMask, font: { color: { rgb: Number(amountCell.v) >= 0 ? emerald : rose }, bold: true } };
    }
    XLSX.utils.book_append_sheet(wb, wsRows, 'Lançamentos');

    const indicatorRows = [
      ['INDICADORES'],
      ['Indicador', 'Valor', 'Observação'],
      ['Receitas', summary.totalCredits, 'Total de entradas no filtro atual'],
      ['Despesas', summary.totalDebits, 'Total de saídas no filtro atual'],
      ['Saldo', summary.balance, 'Receitas menos despesas'],
      ['Transações pendentes', summary.pendingCount, 'Itens ainda não conciliados'],
      ['Transações conciliadas', summary.reconciledCount, 'Itens fechados'],
      ['Transações divergentes', summary.divergentCount, 'Itens com ajuste necessário'],
    ];
    const wsIndicators = XLSX.utils.aoa_to_sheet(indicatorRows);
    wsIndicators['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
    wsIndicators['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 44 }];
    ['A1', 'A2', 'B2', 'C2'].forEach((cell) => { if (wsIndicators[cell]) wsIndicators[cell].s = sectionStyle; });
    ['B3', 'B4', 'B5'].forEach((cell) => { if (wsIndicators[cell]) wsIndicators[cell].s = { numFmt: moneyMask, font: { bold: true } }; });
    XLSX.utils.book_append_sheet(wb, wsIndicators, 'Indicadores');

    const categoryRows = [
      ['CATEGORIAS'],
      ['Categoria', 'Cor', 'Receitas', 'Despesas', 'Saldo', 'Qtd. lançamentos', 'Descrição'],
      ...categoryMetrics.map((category) => [category.name, `#${category.color}`, category.credits, category.debits, category.balance, category.count, category.description]),
    ];
    const wsCategories = XLSX.utils.aoa_to_sheet(categoryRows);
    wsCategories['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];
    wsCategories['!cols'] = [{ wch: 28 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 42 }];
    ['A1', 'A2', 'B2', 'C2', 'D2', 'E2', 'F2', 'G2'].forEach((cell) => { if (wsCategories[cell]) wsCategories[cell].s = sectionStyle; });
    for (let row = 2; row < categoryRows.length; row += 1) {
      const excelRow = row + 1;
      const color = categoryMetrics[row - 2]?.color || emerald;
      const colorCell = wsCategories[`B${excelRow}`];
      if (colorCell) colorCell.s = { fill: { fgColor: { rgb: color } }, font: { color: { rgb: 'FFFFFF' }, bold: true }, alignment: { horizontal: 'center' } };
      ['C', 'D', 'E'].forEach((col) => { if (wsCategories[`${col}${excelRow}`]) wsCategories[`${col}${excelRow}`].s = { numFmt: moneyMask }; });
    }
    XLSX.utils.book_append_sheet(wb, wsCategories, 'Categorias');
    downloadWorkbook(wb, 'controle-de-caixa.xlsx');
  }, [categories, displayedTransactions, selectedAccount, summary]);

  const handleCompanySubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!canManage || !companyForm.name.trim()) return;
    try {
      const payload = serializeCompanyForm(companyForm);
      const { data } = companyForm.id
        ? await FinanceService.updateCompany(companyForm.id, payload)
        : await FinanceService.createCompany(payload);
      setCompanies((prev) => [data, ...prev.filter((company) => company.id !== data.id)].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })));
      setCompanyForm(emptyCompanyForm);
      showAlert('success', companyForm.id ? 'Empresa alterada nas configurações globais.' : 'Empresa salva nas configurações globais.');
    } catch (error) {
      showAlert('error', resolveErrorMessage(error));
    }
  }, [canManage, companyForm, showAlert]);

  const handleDeleteCompany = useCallback(async (companyId) => {
    if (!canManage) return;
    try {
      await FinanceService.deleteCompany(companyId);
      setCompanies((prev) => prev.filter((company) => company.id !== companyId));
      showAlert('success', 'Empresa removida.');
    } catch (error) {
      showAlert('error', resolveErrorMessage(error));
    }
  }, [canManage, showAlert]);

  const handleSaveFinanceSettings = useCallback(async () => {
    if (!canManage) return;
    setSavingFinanceSettings(true);
    try {
      const { data } = await FinanceService.saveSettings({ transactionColumns: visibleTransactionColumns });
      if (Array.isArray(data?.transactionColumns)) setVisibleTransactionColumns(data.transactionColumns);
      showAlert('success', 'Configurações salvas para todos os usuários.');
    } catch (error) {
      showAlert('error', resolveErrorMessage(error));
    } finally {
      setSavingFinanceSettings(false);
    }
  }, [canManage, showAlert, visibleTransactionColumns]);

  const updateAccountability = useCallback((transactionId, field, value) => {
    setAccountability((prev) => ({ ...prev, [transactionId]: { ...(prev[transactionId] || {}), [field]: value } }));
  }, []);

  const handleDownloadReport = useCallback(
    async () => {
      if (!selectedAccountId) return;
      setReportLoading(true);
      try {
        const params = {};
        const fromParam = buildDateParam(fromDate, false);
        const toParam = buildDateParam(toDate, true);
        if (fromParam) params.from = fromParam;
        if (toParam) params.to = toParam;
        const response = await FinanceService.downloadReport(selectedAccountId, params);
        if (typeof window === 'undefined') {
          showAlert('success', 'Relatório gerado.');
          return;
        }
        const blobUrl = window.URL.createObjectURL(response.data);
        const disposition = response.headers?.['content-disposition'] || '';
        const match = /filename="?([^";]+)"?/i.exec(disposition);
        const filename = match ? match[1] : `relatorio-financeiro-${selectedAccountId}.html`;
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        showAlert('success', 'Relatório gerado com sucesso.');
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setReportLoading(false);
      }
    },
    [fromDate, selectedAccountId, showAlert, toDate],
  );

  if (!canView) {
    return (
      <div className="p-6">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
          <FaExclamationTriangle className="mr-2 inline h-5 w-5 align-text-bottom" />
          Você não possui permissão para acessar o módulo financeiro.
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 p-6 ${isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>
      <div className="flex flex-col justify-between gap-4 rounded-3xl bg-emerald-900 p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <FaMoneyCheckAlt className="h-7 w-7 text-white" />
            Financeiro
          </h1>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={handleOpenCategoryManager}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 ${
              isDarkTheme
                ? 'border-emerald-700 bg-slate-900 text-emerald-300 hover:bg-slate-800'
                : 'border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            <FaSitemap className="h-4 w-4" />
            Abrir gerenciador de categorias
          </button>
          {canManage && (
            <button
              type="button"
              onClick={handleCreateAccount}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <FaPlus className="h-4 w-4" />
              Nova conta bancária
            </button>
          )}
        </div>
      </div>

      {alert && (
        <div
          className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
            alert.type === 'error'
              ? 'border-rose-200 bg-rose-50 text-rose-700'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700'
          }`}
        >
          {alert.type === 'error' ? (
            <FaExclamationTriangle className="mt-0.5 h-4 w-4" />
          ) : (
            <FaCheckCircle className="mt-0.5 h-4 w-4" />
          )}
          <span>{alert.message}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        {FINANCE_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setActiveFinanceTab(tab.value)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-emerald-300 ${
              activeFinanceTab === tab.value
                ? 'bg-emerald-600 text-white shadow'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>


      <input
        ref={fileInputRef}
        type="file"
        accept=".ofx,.xlsx,.xls,application/x-ofx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="hidden"
        onChange={handleOfxFileChange}
      />

      <div className="space-y-6">
        {activeFinanceTab === 'accounts' && (
        <div
          className={`rounded-2xl border shadow-sm ${
            isDarkTheme ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'
          }`}
        >
          <div
            className={`flex items-center justify-between border-b px-4 py-3 ${
              isDarkTheme ? 'border-slate-700' : 'border-slate-100'
            }`}
          >
            <h2 className={`text-sm font-semibold uppercase tracking-wide ${isDarkTheme ? 'text-slate-300' : 'text-slate-600'}`}>Contas bancárias</h2>
            {loadingAccounts && <span className={`text-xs ${isDarkTheme ? 'text-slate-500' : 'text-slate-400'}`}>Carregando…</span>}
          </div>
          <div className="max-h-[480px] space-y-1 overflow-y-auto p-2">
            {accounts.map((account) => (
              <button
                key={account.id}
                type="button"
                onClick={() => handleSelectAccount(account.id)}
                className={`w-full rounded-xl px-4 py-3 text-left transition focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  selectedAccountId === account.id
                    ? isDarkTheme
                      ? 'bg-emerald-900/30 text-emerald-200 shadow'
                      : 'bg-emerald-50 text-emerald-800 shadow'
                    : isDarkTheme
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaUniversity className="h-4 w-4" />
                    <div>
                      <p className="text-sm font-semibold">{account.name}</p>
                      <p className={`text-xs ${isDarkTheme ? 'text-slate-300' : 'text-slate-500'}`}>{account.bankName}</p>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEditAccount(account);
                        }}
                        className={`rounded-full p-2 text-xs shadow focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                          isDarkTheme
                            ? 'bg-slate-900/80 text-slate-300 hover:text-emerald-300'
                            : 'bg-white/80 text-slate-500 hover:text-emerald-600'
                        }`}
                      >
                        <FaSave className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        disabled={deletingAccountId === account.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteAccount(account.id);
                        }}
                        className={`rounded-full p-2 text-xs shadow focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:opacity-50 ${
                          isDarkTheme
                            ? 'bg-slate-900/80 text-rose-300 hover:bg-rose-900/30'
                            : 'bg-white/80 text-rose-500 hover:bg-rose-50'
                        }`}
                      >
                        <FaTrash className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
                <p className={`mt-2 text-xs ${isDarkTheme ? 'text-slate-300' : 'text-slate-500'}`}>
                  Agência {account.agencyNumber || '-'} • Conta {account.accountNumber}
                </p>
              </button>
            ))}
            {!loadingAccounts && accounts.length === 0 && (
              <div
                className={`rounded-xl border border-dashed p-4 text-center text-sm ${
                  isDarkTheme ? 'border-slate-600 text-slate-300' : 'border-slate-200 text-slate-500'
                }`}
              >
                Nenhuma conta cadastrada.
                {canManage && ' Clique em "Nova conta bancária" para adicionar a primeira.'}
              </div>
            )}
            {accountsError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                {accountsError}
              </div>
            )}
          </div>
        </div>
        )}

        {activeFinanceTab === 'transactions' && (
        <div className="space-y-4">
          {selectedAccount ? (
            <>
              {accounts.length > 1 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Alterar conta
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Selecionar conta bancária para transações">
                    {accounts.map((account) => (
                      <button
                        key={`transaction-account-${account.id}`}
                        type="button"
                        onClick={() => handleSelectAccount(account.id)}
                        className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-emerald-300 ${
                          selectedAccountId === account.id
                            ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {account.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">{selectedAccount.name}</h2>
                    <p className="text-sm text-slate-500">
                      {selectedAccount.bankName} • Agência {selectedAccount.agencyNumber || '-'} • Conta {selectedAccount.accountNumber}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">{integrationLabel}</p>
                    {selectedAccount.description && (
                      <p className="mt-3 max-w-2xl text-sm text-slate-600 whitespace-pre-line">
                        {selectedAccount.description}
                      </p>
                    )}
                    <div className="mt-3 max-w-2xl">
                      <OpenFinanceConnectionStatus
                        account={selectedAccount}
                        refreshKey={openFinanceStatusVersion}
                        canManage={canManage}
                        showAlert={showAlert}
                        autoStart={openFinanceAutoStartAccountId === selectedAccount.id}
                        onAutoStart={() => setOpenFinanceAutoStartAccountId(null)}
                        onConnected={async () => {
                          await loadTransactions();
                          setOpenFinanceStatusVersion((version) => version + 1);
                        }}
                        onTransactionsRefresh={loadTransactions}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canManage && !isOpenFinanceAccount && (
                      <button
                        type="button"
                        onClick={handleOpenManualForm}
                        disabled={creatingManualTransaction}
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-4 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:opacity-50"
                      >
                        <FaKeyboard className="h-3.5 w-3.5" />
                        Lançar transação manual
                      </button>
                    )}
                    {canManage && isOpenFinanceAccount && (
                      <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700">
                        <FaLock className="h-3 w-3" /> Origem protegida pela integração bancária
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleDownloadReport}
                      disabled={reportLoading}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
                    >
                      <FaFileDownload className="h-3.5 w-3.5" />
                      {reportLoading ? 'Gerando…' : 'Exportar relatório'}
                    </button>
                    <button
                      type="button"
                      onClick={handleExportCashControl}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      <FaFileExcel className="h-3.5 w-3.5" />
                      Controle de Caixa
                    </button>
                    <button
                      type="button"
                      onClick={handleOpenCategoryReport}
                      disabled={!selectedAccountId || categoryReportLoading}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
                    >
                      <FaChartPie className="h-3.5 w-3.5" />
                      {categoryReportLoading ? 'Carregando…' : 'Resumo por categoria'}
                    </button>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total créditos</p>
                    <p className="mt-2 text-lg font-semibold text-emerald-700">{formatCurrency(summary.totalCredits)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total débitos</p>
                    <p className="mt-2 text-lg font-semibold text-rose-600">{formatCurrency(summary.totalDebits)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Saldo</p>
                    <p className={`mt-2 text-lg font-semibold ${summary.balance >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      {formatCurrency(summary.balance)}
                    </p>
                  </div>
                </div>
              </div>

              {canManage && !isOpenFinanceAccount && showManualTransactionForm && (
                <div className="rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">Nova transação manual</h3>
                    <button
                      type="button"
                      onClick={handleCloseManualForm}
                      className="text-xs font-medium text-slate-500 underline-offset-2 hover:underline"
                    >
                      Cancelar lançamento
                    </button>
                  </div>
                  <form className="mt-4 space-y-4" onSubmit={handleManualFormSubmit}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-type">
                          Tipo
                        </label>
                        <select
                          id="manual-transaction-type"
                          name="type"
                          value={manualFormState.type}
                          onChange={handleManualFormChange}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          required
                        >
                          {MANUAL_TRANSACTION_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-amount">
                          Valor (BRL)
                        </label>
                        <input
                          id="manual-transaction-amount"
                          name="amount"
                          value={manualFormState.amount}
                          onChange={handleManualFormChange}
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-occurred-at">
                          Data
                        </label>
                        <input
                          id="manual-transaction-occurred-at"
                          name="occurredAt"
                          value={manualFormState.occurredAt}
                          onChange={handleManualFormChange}
                          type="date"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          required
                        />
                      </div>
                      <div className="space-y-2 md:col-span-3 lg:col-span-1">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-category">
                          Categoria
                        </label>
                        <select
                          id="manual-transaction-category"
                          name="categoryId"
                          value={manualFormState.categoryId}
                          onChange={handleManualFormChange}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        >
                          <option value="">Sem categoria</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-3 lg:col-span-1">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-subcategory">
                          Subcategoria
                        </label>
                        <select
                          id="manual-transaction-subcategory"
                          name="subcategoryId"
                          value={manualFormState.subcategoryId}
                          onChange={handleManualFormChange}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          disabled={
                            !manualFormState.categoryId || manualFormSubcategories.length === 0
                          }
                        >
                          <option value="">Sem subcategoria</option>
                          {manualFormSubcategories.map((subcategory) => (
                            <option key={subcategory.id} value={subcategory.id}>
                              {subcategory.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-document">Nº do documento</label>
                      <input id="manual-transaction-document" name="documentNumber" value={manualFormState.documentNumber} onChange={handleManualFormChange} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200" placeholder="NF, recibo, guia, holerite..." />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-description">
                        Descrição
                      </label>
                      <input
                        id="manual-transaction-description"
                        name="description"
                        value={manualFormState.description}
                        onChange={handleManualFormChange}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        placeholder="Descrição da movimentação"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-notes">
                        Observações
                      </label>
                      <textarea
                        id="manual-transaction-notes"
                        name="notes"
                        value={manualFormState.notes}
                        onChange={handleManualFormChange}
                        rows={3}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        placeholder="Informações adicionais sobre o lançamento"
                      />
                    </div>
                    {manualFormError && (
                      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                        {manualFormError}
                      </div>
                    )}
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={handleCloseManualForm}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={creatingManualTransaction}
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                      >
                        <FaSave className="h-4 w-4" />
                        {creatingManualTransaction ? 'Salvando…' : 'Salvar transação'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="flex flex-wrap gap-3">
                    <div className="space-y-1">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-from-date">
                        De
                      </label>
                      <input
                        id="finance-from-date"
                        type="date"
                        value={fromDate}
                        onChange={(event) => setFromDate(event.target.value)}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-to-date">
                        Até
                      </label>
                      <input
                        id="finance-to-date"
                        type="date"
                        value={toDate}
                        onChange={(event) => setToDate(event.target.value)}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-category-filter">
                        Categoria
                      </label>
                      <select
                        id="finance-category-filter"
                        value={categoryFilter}
                        onChange={handleCategoryFilterChange}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      >
                        <option value="">Todas</option>
                        <option value="none">Sem categoria</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-subcategory-filter">
                        Subcategoria
                      </label>
                      <select
                        id="finance-subcategory-filter"
                        value={subcategoryFilter}
                        onChange={handleSubcategoryFilterChange}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        disabled={!categoryFilter}
                      >
                        <option value="">Todas</option>
                        <option value="none">Sem subcategoria</option>
                        {filterSubcategories.map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex flex-1 items-end gap-3">
                    <div className="w-full flex-1">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-search">
                        Buscar
                      </label>
                      <input
                        id="finance-search"
                        type="search"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Descrição, identificador ou nota"
                        className="mt-1 w-full rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setStatusFilter('all');
                        setFromDate('');
                        setToDate('');
                        setSearchTerm('');
                        setCategoryFilter('');
                        setSubcategoryFilter('');
                      }}
                      className="h-10 rounded-full border border-slate-200 px-4 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      Limpar filtros
                    </button>
                  </div>
                </div>
                {transactionsError && (
                  <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                    {transactionsError}
                  </div>
                )}
                {canManage && !isOpenFinanceAccount && selectedTransactionIds.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-3">
                    <span className="text-sm font-semibold text-rose-700">{selectedTransactionIds.length} transações selecionadas para exclusão</span>
                    <button type="button" onClick={handleDeleteSelectedTransactions} className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-xs font-semibold text-white shadow hover:bg-rose-700 disabled:opacity-50">
                      <FaTrash className="h-3 w-3" /> Excluir selecionadas
                    </button>
                  </div>
                )}

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-wide text-slate-500">
                        {canManage && !isOpenFinanceAccount && (
                          <th className="px-3 py-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                              checked={
                                displayedTransactions.length > 0 &&
                                displayedTransactions.every((transaction) => selectedIdsSet.has(transaction.id))
                              }
                              onChange={handleSelectAllDisplayed}
                            />
                          </th>
                        )}
                        {visibleTransactionColumns.includes('occurredAt') && <th className="px-3 py-2">Data</th>}
                        {visibleTransactionColumns.includes('description') && <th className="px-3 py-2">Descrição</th>}
                        {visibleTransactionColumns.includes('documentNumber') && <th className="px-3 py-2">Nº doc.</th>}
                        {visibleTransactionColumns.includes('amount') && <th className="px-3 py-2 text-right">Valor</th>}
                        {visibleTransactionColumns.includes('type') && <th className="px-3 py-2">Tipo</th>}
                        {visibleTransactionColumns.includes('source') && <th className="px-3 py-2">Origem</th>}
                        {visibleTransactionColumns.includes('category') && <th className="px-3 py-2">Categoria</th>}
                        {visibleTransactionColumns.includes('subcategory') && <th className="px-3 py-2">Subcategoria</th>}
                        {canManage && <th className="px-3 py-2 text-right">Ações</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {transactionsLoading ? (
                        <tr>
                          <td colSpan={canManage ? 8 : 6} className="px-3 py-6 text-center text-sm text-slate-500">
                            Carregando transações…
                          </td>
                        </tr>
                      ) : displayedTransactions.length === 0 ? (
                        <tr>
                          <td colSpan={canManage ? 8 : 6} className="px-3 py-6 text-center text-sm text-slate-500">
                            Nenhuma transação encontrada para os filtros selecionados.
                          </td>
                        </tr>
                      ) : (
                        displayedTransactions.map((transaction) => {
                          const isEditing = editingTransactionId === transaction.id;
                          const isDeleting = deletingIdsSet.has(transaction.id);
                          const editDisabled =
                            updatingTransaction ||
                            (editingTransactionId !== null && !isEditing);

                          return (
                            <tr key={transaction.id} className="text-sm text-slate-600">
                              {canManage && !isOpenFinanceAccount && (
                                <td className="px-3 py-3 align-middle">
                                  <input
                                    type="checkbox"
                                    checked={selectedIdsSet.has(transaction.id)}
                                  onChange={() => handleToggleTransactionSelection(transaction.id)}
                                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                />
                              </td>
                            )}
                              {visibleTransactionColumns.includes('occurredAt') && <td className="px-3 py-3 align-middle text-slate-500">
                                {formatDateTime(transaction.occurredAt)}
                              </td>}
                              {visibleTransactionColumns.includes('description') && <td className="px-3 py-3 align-middle">
                                {isEditing ? (
                                  <div className="flex flex-col gap-3">
                                    {!isOpenFinanceAccount && <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                      <div className="space-y-1">
                                        <label
                                          className="text-xs font-semibold uppercase tracking-wide text-emerald-700"
                                          htmlFor={`finance-transaction-category-${transaction.id}`}
                                        >
                                          Categoria
                                        </label>
                                        <select
                                          id={`finance-transaction-category-${transaction.id}`}
                                          className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-sm text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                          value={editingTransactionCategoryId}
                                          onChange={handleEditingTransactionCategoryChange}
                                          disabled={updatingTransaction}
                                        >
                                          <option value="">Sem categoria</option>
                                          {categories.map((category) => (
                                            <option key={category.id} value={category.id}>
                                              {category.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="space-y-1">
                                        <label
                                          className="text-xs font-semibold uppercase tracking-wide text-emerald-700"
                                          htmlFor={`finance-transaction-subcategory-${transaction.id}`}
                                        >
                                          Subcategoria
                                        </label>
                                        <select
                                          id={`finance-transaction-subcategory-${transaction.id}`}
                                          className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-sm text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                          value={editingTransactionSubcategoryId}
                                          onChange={handleEditingTransactionSubcategoryChange}
                                          disabled={
                                            updatingTransaction ||
                                            !editingTransactionCategoryId ||
                                            editingSubcategories.length === 0
                                          }
                                        >
                                          <option value="">Sem subcategoria</option>
                                          {editingSubcategories.map((subcategory) => (
                                            <option key={subcategory.id} value={subcategory.id}>
                                              {subcategory.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>}
                                    {!isOpenFinanceAccount && <input
                                      value={editingTransactionDocumentNumber}
                                      onChange={(event) => setEditingTransactionDocumentNumber(event.target.value)}
                                      placeholder="Nº do documento"
                                      className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-sm text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                      disabled={updatingTransaction}
                                    />}
                                    <textarea
                                      value={editingTransactionDescription}
                                      onChange={(event) =>
                                        setEditingTransactionDescription(event.target.value)
                                      }
                                      rows={2}
                                      className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                      disabled={updatingTransaction}
                                      autoFocus
                                      onKeyDown={(event) => {
                                        if (event.key === 'Escape') {
                                          event.preventDefault();
                                          handleCancelEditingTransaction();
                                        }
                                      }}
                                    />
                                    {isOpenFinanceAccount && (
                                      <p className="inline-flex items-center gap-2 text-xs font-medium text-sky-700">
                                        <FaLock className="h-3 w-3" /> Somente a descrição pode ser alterada. Valor e tipo são definidos pelo banco.
                                      </p>
                                    )}
                                    <div className="text-xs text-slate-400">
                                      {transaction.fitId && (
                                        <span className="mr-2">FIT: {transaction.fitId}</span>
                                      )}
                                      {transaction.externalId && <span>Ext: {transaction.externalId}</span>}
                                    </div>
                                    {editingTransactionError && (
                                      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">
                                        {editingTransactionError}
                                      </div>
                                    )}
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={handleUpdateTransaction}
                                        disabled={updatingTransaction}
                                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                                      >
                                        {updatingTransaction ? 'Salvando…' : 'Salvar alterações'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleCancelEditingTransaction}
                                        disabled={updatingTransaction}
                                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="font-medium text-slate-700">
                                      {transaction.description || 'Sem descrição'}
                                    </div>
                                    <div className="text-xs text-slate-400">
                                      {transaction.fitId && (
                                        <span className="mr-2">FIT: {transaction.fitId}</span>
                                      )}
                                      {transaction.externalId && <span>Ext: {transaction.externalId}</span>}
                                    </div>
                                  </>
                                )}
                              </td>}
                              {visibleTransactionColumns.includes('documentNumber') && <td className="px-3 py-3 align-middle text-slate-500">{transaction.documentNumber || '-'}</td>}
                              {visibleTransactionColumns.includes('amount') && <td className="px-3 py-3 align-middle text-right font-semibold">
                                <span className={transaction.amount >= 0 ? 'text-emerald-700' : 'text-rose-600'}>
                                  {formatCurrency(transaction.amount)}
                                </span>
                              </td>}
                              {visibleTransactionColumns.includes('type') && <td className="px-3 py-3 align-middle text-slate-500">{TRANSACTION_TYPE_LABELS[transaction.type] || transaction.type || '-'}</td>}
                              {visibleTransactionColumns.includes('source') && <td className="px-3 py-3 align-middle text-slate-500">
                                {SOURCE_LABELS[transaction.source] || transaction.source || '-'}
                              </td>}
                              {visibleTransactionColumns.includes('category') && <td className="px-3 py-3 align-middle text-slate-500">{transaction.category?.name || '-'}</td>}
                              {visibleTransactionColumns.includes('subcategory') && <td className="px-3 py-3 align-middle text-slate-500">{transaction.subcategory?.name || '-'}</td>}
                              {canManage && (
                                <td className="px-3 py-3 align-middle text-right">
                                  <div className="flex flex-wrap justify-end gap-2">
                                    {isEditing ? (
                                      <span className="text-xs font-semibold text-emerald-600">Editando…</span>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleStartEditingTransaction(transaction)}
                                        disabled={editDisabled}
                                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
                                      >
                                        <FaEdit className="h-3 w-3" />
                                        Editar
                                      </button>
                                    )}
                                    {!isOpenFinanceAccount && <button
                                      type="button"
                                      onClick={() => handleDeleteTransaction(transaction.id)}
                                      disabled={
                                        isDeleting ||
                                        transaction.status === 'RECONCILED' ||
                                        updatingTransaction
                                      }
                                      className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:opacity-50"
                                    >
                                      <FaTrash className="h-3 w-3" />
                                      {isDeleting ? 'Removendo…' : 'Excluir'}
                                    </button>}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {transactionPagination.total > 0 && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                    <span>
                      Página {transactionPagination.page} de {transactionPagination.totalPages} · {transactionPagination.total} movimentações
                    </span>
                    <div className="flex gap-2">
                      <button type="button" disabled={transactionPage <= 1 || transactionsLoading} onClick={() => setTransactionPage((page) => Math.max(1, page - 1))} className="rounded-full border border-slate-200 px-4 py-2 disabled:opacity-50">Anterior</button>
                      <button type="button" disabled={transactionPage >= transactionPagination.totalPages || transactionsLoading} onClick={() => setTransactionPage((page) => Math.min(transactionPagination.totalPages, page + 1))} className="rounded-full border border-slate-200 px-4 py-2 disabled:opacity-50">Próxima</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center">
              <p className="text-sm text-slate-500">
                Selecione uma conta bancária para visualizar as transações e iniciar a conciliação.
              </p>
            </div>
          )}
        </div>
        )}


        {activeFinanceTab === 'categorySummary' && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Resumo por categoria</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => loadCategoryReport('all')} disabled={accounts.length === 0 || categoryReportLoading} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50">
                  <FaSync className={`h-3.5 w-3.5 ${categoryReportLoading ? 'animate-spin' : ''}`} />
                  {categoryReportLoading ? 'Carregando…' : 'Atualizar resumo'}
                </button>
              </div>
            </div>
            {accounts.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">Cadastre uma conta bancária para visualizar o resumo geral por categoria.</div>
            ) : categoryReportError ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{categoryReportError}</div>
            ) : categoryReportLoading && !hasCategoryReportData ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">Carregando resumo financeiro…</div>
            ) : hasCategoryReportData ? (
              <div className="mt-4 space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Total de receitas</p><p className="mt-2 text-lg font-semibold text-emerald-700">{formatCurrency(categoryReportTotals.credits)}</p></div>
                  <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-rose-600">Total de despesas</p><p className="mt-2 text-lg font-semibold text-rose-600">{formatCurrency(categoryReportTotals.debits)}</p></div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Saldo do período</p><p className={`mt-2 text-lg font-semibold ${categoryReportTotals.balance >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{formatCurrency(categoryReportTotals.balance)}</p></div>
                </div>
                {(categoryMovementChart || categoryBalanceChart) && (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {categoryMovementChart && (
                      <div className="rounded-2xl border border-slate-200 p-4">
                        <h4 className="text-sm font-semibold text-slate-700">Distribuição das movimentações</h4>
                        <div className="mt-4 h-64">
                          <Doughnut data={categoryMovementChart} options={categoryMovementChartOptions} />
                        </div>
                      </div>
                    )}
                    {categoryBalanceChart && (
                      <div className="rounded-2xl border border-slate-200 p-4">
                        <h4 className="text-sm font-semibold text-slate-700">Receitas x despesas por categoria</h4>
                        <div className="mt-4 h-64">
                          <Bar data={categoryBalanceChart} options={categoryBalanceChartOptions} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {topSubcategoryChart && (
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <h4 className="text-sm font-semibold text-slate-700">Subcategorias com maior movimentação</h4>
                    <div className="mt-4 h-72">
                      <Bar data={topSubcategoryChart} options={topSubcategoryChartOptions} />
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-100 text-left text-sm"><thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Categoria</th><th className="px-4 py-3 text-right">Transações</th><th className="px-4 py-3 text-right">Receitas</th><th className="px-4 py-3 text-right">Despesas</th><th className="px-4 py-3 text-right">Saldo</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">{categoryReportData.categories.map((category) => (<tr key={`summary-${category.id ?? category.name}`} className="text-sm text-slate-700"><td className="px-4 py-3 font-semibold">{category.name}</td><td className="px-4 py-3 text-right">{category.transactionCount}</td><td className="px-4 py-3 text-right text-emerald-700">{formatCurrency(category.totalCredits)}</td><td className="px-4 py-3 text-right text-rose-600">{formatCurrency(category.totalDebits)}</td><td className={`px-4 py-3 text-right font-semibold ${category.balance >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{formatCurrency(category.balance)}</td></tr>))}</tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">Nenhuma movimentação encontrada para gerar o relatório.</div>
            )}
          </div>
        )}


        {activeFinanceTab === 'accountability' && (
          <div className="space-y-4">
            <div className="rounded-3xl border border-emerald-100 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    <FaClipboardList /> Prestação de contas
                  </span>
                </div>
              </div>

              {selectedAccountabilityTransaction ? (
                <div className="mt-5 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-start gap-3">
                      <span className="rounded-2xl bg-white p-3 text-emerald-700 shadow-sm"><FaReceipt /></span>
                      <div>
                        <h3 className="font-semibold text-slate-900">{selectedAccountabilityTransaction.description || 'Transação sem descrição'}</h3>
                        <p className="text-sm text-slate-500">{formatDateTime(selectedAccountabilityTransaction.occurredAt)} • {selectedAccountabilityTransaction.documentNumber || 'Sem nº de documento'}</p>
                      </div>
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      <label className="space-y-1 text-sm font-medium text-slate-600">
                        Status
                        <select disabled={isOpenFinanceAccount} title={isOpenFinanceAccount ? 'Status protegido para transações sincronizadas' : undefined} value={selectedAccountabilityTransaction.status} onChange={(e) => FinanceService.updateTransaction(selectedAccountId, selectedAccountabilityTransaction.id, { status: e.target.value }).then(loadTransactions)} className="w-full rounded-xl border border-slate-200 px-3 py-2 disabled:bg-slate-100 disabled:text-slate-500">
                          <option value="PENDING">Pendente</option><option value="RECONCILED">Conciliada</option><option value="DIVERGENT">Divergente</option>
                        </select>
                        <span className="mt-2 block rounded-xl bg-white px-3 py-2 text-lg font-bold text-slate-900">{formatCurrency(selectedAccountabilityTransaction.amount)}</span>
                      </label>
                      <label className="space-y-1 text-sm font-medium text-slate-600 sm:col-span-2">
                        Empresa
                        <div className="flex gap-2">
                          <select value={selectedAccountabilityData.companyId || ''} onChange={(e) => updateAccountability(selectedAccountabilityTransaction.id, 'companyId', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2">
                            <option value="">Selecione uma empresa</option>{companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                          </select>
                          {selectedAccountabilityCompany && <button type="button" onClick={() => setSelectedCompany(selectedAccountabilityCompany)} className="rounded-xl border border-slate-200 px-3 text-slate-600 hover:bg-slate-50"><FaSearch /></button>}
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-emerald-700"><FaFileInvoice /> Documento comprobatório</div>
                    <div className="grid gap-3">
                      <label className="space-y-1 text-sm font-medium text-slate-600">
                        Tipo de documento
                        <select value={selectedAccountabilityDocumentType} onChange={(e) => updateAccountability(selectedAccountabilityTransaction.id, 'documentType', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2">
                          {DOCUMENT_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                      </label>
                      {selectedAccountabilityDocumentType === 'NFE' && (
                        <label className="space-y-1 text-sm font-medium text-slate-600">
                          Chave de acesso da NF-e
                          <input value={selectedAccountabilityData.accessKey || ''} onChange={(e) => updateAccountability(selectedAccountabilityTransaction.id, 'accessKey', e.target.value)} placeholder="44 dígitos da chave eletrônica" className="w-full rounded-xl border border-slate-200 px-3 py-2" />
                        </label>
                      )}
                      {['HOLERITE', 'FATURA', 'ENCARGOS'].includes(selectedAccountabilityDocumentType) && (
                        <label className="space-y-1 text-sm font-medium text-slate-600">
                          Referência
                          <input value={selectedAccountabilityData.reference || ''} onChange={(e) => updateAccountability(selectedAccountabilityTransaction.id, 'reference', e.target.value)} placeholder="Ex.: 07/2024" className="w-full rounded-xl border border-slate-200 px-3 py-2" />
                        </label>
                      )}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="space-y-1 text-sm font-medium text-slate-600">Nº do documento<input value={selectedAccountabilityData.invoiceNumber || selectedAccountabilityTransaction.documentNumber || ''} onChange={(e) => updateAccountability(selectedAccountabilityTransaction.id, 'invoiceNumber', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2" /></label>
                        <label className="space-y-1 text-sm font-medium text-slate-600">Data de emissão<input type="date" value={selectedAccountabilityData.issueDate || ''} onChange={(e) => updateAccountability(selectedAccountabilityTransaction.id, 'issueDate', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2" /></label>
                      </div>
                      <label className="space-y-1 text-sm font-medium text-slate-600">Justificativa<textarea value={selectedAccountabilityData.justification || ''} onChange={(e) => updateAccountability(selectedAccountabilityTransaction.id, 'justification', e.target.value)} rows={3} placeholder="Explique a finalidade da despesa e qualquer divergência." className="w-full rounded-xl border border-slate-200 px-3 py-2" /></label>
                      <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-800">
                        <span className="flex items-center gap-2 font-semibold"><FaFileImport /> Anexos da documentação</span>
                        <input
                          type="file"
                          accept=".xml,.pdf,.jpg,.jpeg,.png"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            const currentAttachments = Array.isArray(selectedAccountabilityData.attachments) ? selectedAccountabilityData.attachments : [];
                            const nextAttachments = [
                              ...currentAttachments,
                              ...files.map((file) => ({ id: `${Date.now()}-${file.name}`, name: file.name, url: URL.createObjectURL(file) })),
                            ];
                            updateAccountability(selectedAccountabilityTransaction.id, 'attachments', nextAttachments);
                            e.target.value = '';
                          }}
                          className="mt-3 block w-full text-xs"
                        />
                        <div className="mt-3 space-y-2">
                          {(Array.isArray(selectedAccountabilityData.attachments) && selectedAccountabilityData.attachments.length > 0) ? selectedAccountabilityData.attachments.map((attachment) => (
                            <div key={attachment.id || attachment.name} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white px-3 py-2 text-slate-700 shadow-sm">
                              <span className="truncate text-xs font-medium">{attachment.name}</span>
                              <div className="flex gap-2">
                                {attachment.url && <button type="button" onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')} className="rounded-full border border-emerald-200 px-3 py-1 text-xs text-emerald-700">Visualizar</button>}
                                <button type="button" onClick={() => updateAccountability(selectedAccountabilityTransaction.id, 'attachments', (selectedAccountabilityData.attachments || []).filter((item) => item !== attachment))} className="rounded-full border border-rose-200 px-3 py-1 text-xs text-rose-600">Excluir</button>
                              </div>
                            </div>
                          )) : <small className="block text-emerald-700">Adicione XML da NF-e, PDF, imagem ou guia digital. Depois você poderá visualizar ou excluir cada anexo.</small>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">Nenhuma transação encontrada para prestar contas.</div>
              )}
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div><h3 className="font-semibold text-slate-900">Despesas e transações</h3><p className="text-sm text-slate-500">Clique em uma linha para ver e preencher os dados acima.</p></div>
                <FaInfoCircle className="text-slate-400" />
              </div>
              <div className="mb-4 flex flex-wrap gap-2">{[['entries', 'Entradas'], ['exits', 'Saídas']].map(([key, label]) => <button key={key} type="button" onClick={() => setAccountabilityKind(key)} className={`rounded-full border px-4 py-2 text-sm font-semibold ${accountabilityKind === key ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{label}</button>)}</div>
              <div className="overflow-x-auto rounded-2xl border border-slate-100">
                <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>{canManage && <th className="px-4 py-3">Conciliar</th>}<th className="px-4 py-3">Data</th><th className="px-4 py-3">Descrição</th><th className="px-4 py-3">Documento</th><th className="px-4 py-3 text-right">Valor</th><th className="px-4 py-3">Status</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {displayedTransactions.filter((tx) => accountabilityKind === 'entries' ? tx.amount >= 0 : tx.amount < 0).map((tx) => <tr key={`acct-row-${tx.id}`} onClick={() => setSelectedAccountabilityTransactionId(tx.id)} className={`cursor-pointer hover:bg-emerald-50 ${selectedAccountabilityTransaction?.id === tx.id ? 'bg-emerald-50' : ''}`}>{canManage && <td className="px-4 py-3"><input type="checkbox" checked={selectedIdsSet.has(tx.id)} onClick={(event) => event.stopPropagation()} onChange={() => handleToggleTransactionSelection(tx.id)} className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" /></td>}<td className="px-4 py-3 text-slate-500">{formatDateTime(tx.occurredAt)}</td><td className="px-4 py-3 font-medium text-slate-700">{tx.description || 'Sem descrição'}</td><td className="px-4 py-3 text-slate-500">{tx.documentNumber || '-'}</td><td className={`px-4 py-3 text-right font-semibold ${tx.amount >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{formatCurrency(tx.amount)}</td><td className="px-4 py-3"><span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[tx.status] || 'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[tx.status] || tx.status}</span></td></tr>)}
                  </tbody>
                </table>
              </div>
              {canManage && (
                <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div><p className="text-sm font-semibold text-slate-700">Selecionadas {selectionStats.count} transações</p><p className="text-xs text-slate-500">Total selecionado: {formatCurrency(selectionStats.total)} • Créditos: {selectionStats.credits} • Débitos: {selectionStats.debits}</p></div>
                    <div className="flex flex-wrap gap-2"><button type="button" onClick={handleSelectAllDisplayed} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100">Selecionar exibidas</button><button type="button" onClick={handleClearSelection} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100">Limpar seleção</button></div>
                  </div>
                  <div className="mt-4 space-y-3"><label className="space-y-1 text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-accountability-reconciliation-notes">Observações da conciliação<textarea id="finance-accountability-reconciliation-notes" value={reconciliationNotes} onChange={(event) => setReconciliationNotes(event.target.value)} rows={3} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-normal normal-case tracking-normal focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200" placeholder="Anote divergências ou observações relevantes" /></label><button type="button" onClick={handleReconcile} disabled={reconciling || selectionStats.count === 0} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 disabled:opacity-50"><FaCheckCircle className="h-4 w-4" />{reconciling ? 'Conciliando…' : 'Concluir conciliação'}</button></div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeFinanceTab === 'expenseForecast' && <ExpenseForecastPanel canManage={canManage} showAlert={showAlert} />}

        {activeFinanceTab === 'insights' && (
          <AIInsightsPanel
            module="finance"
            title="Insights inteligentes do financeiro"
            subtitle="Análise das contas, transações, conciliações, categorias e resumo financeiro disponível."
            data={{ accounts, transactions, transactionSummary, categoryReportData }}
          />
        )}

        {activeFinanceTab === 'settings' && (
          <div className="space-y-4">
            <div className="rounded-2xl border bg-white p-4 shadow-sm">
              <div className="flex flex-wrap gap-2">
                {[['columns', 'Configurações de colunas', FaCog], ['accounts', 'Contas bancárias', FaUniversity], ['companies', 'Cadastro de empresas', FaBuilding], ['imports', 'Importação OFX/Excel e Template', FaFileImport], ['expenses', 'Lembrete de despesas', FaReceipt]].map(([key, label, Icon]) => (
                  <button key={key} type="button" onClick={() => setSettingsSection(key)} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${settingsSection === key ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}><Icon /> {label}</button>
                ))}
              </div>
            </div>

            {settingsSection === 'columns' && (
              <div className="rounded-2xl border bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div><h2 className="text-xl font-semibold"><FaCog className="inline"/> Configurações de colunas</h2><p className="text-sm text-slate-500">As colunas salvas aqui ficam disponíveis para todos os usuários.</p></div>
                  {canManage && <button type="button" onClick={handleSaveFinanceSettings} disabled={savingFinanceSettings} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 disabled:opacity-50"><FaSave /> {savingFinanceSettings ? 'Salvando…' : 'Salvar configurações'}</button>}
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-3">{TRANSACTION_COLUMN_OPTIONS.map(([key,label]) => <label key={key} className="rounded-xl border p-3 text-sm"><input type="checkbox" checked={visibleTransactionColumns.includes(key)} onChange={(e) => setVisibleTransactionColumns((prev) => e.target.checked ? [...new Set([...prev,key])] : prev.filter((item) => item !== key))}/> {label}</label>)}</div>
              </div>
            )}

            {settingsSection === 'accounts' && (
              <div className="rounded-2xl border bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3"><h2 className="text-xl font-semibold"><FaUniversity className="inline"/> Contas bancárias</h2>{canManage && <button type="button" onClick={handleCreateAccount} className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white"><FaPlus className="inline"/> Nova conta</button>}</div>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {accounts.map((account) => (
                    <div
                      key={`settings-account-${account.id}`}
                      className="rounded-2xl border border-slate-200 p-4"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-slate-900">{account.name}</h3>
                          <p className="text-sm text-slate-500">
                            {account.bankName} • Ag. {account.agencyNumber || '-'} • Conta {account.accountNumber}
                          </p>
                        </div>
                        {canManage && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditAccount(account)}
                              className="rounded-full border px-3 py-1 text-xs text-emerald-700"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteAccount(account.id)}
                              className="rounded-full border border-rose-200 px-3 py-1 text-xs text-rose-600"
                            >
                              Excluir
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-slate-400">
                        {INTEGRATION_TYPES.find((item) => item.value === account.integrationType)?.label || 'Integração personalizada'}
                      </p>
                      <div className="mt-3 space-y-2">
                        <OpenFinanceConnectionStatus
                          account={account}
                          refreshKey={openFinanceStatusVersion}
                          canManage={canManage}
                          showAlert={showAlert}
                          onConnected={() => {
                            setSelectedAccountId(account.id);
                            setOpenFinanceStatusVersion((version) => version + 1);
                          }}
                          onTransactionsRefresh={
                            account.id === selectedAccountId
                              ? loadTransactions
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {settingsSection === 'companies' && (
              <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
                <form onSubmit={handleCompanySubmit} className="space-y-3 rounded-2xl border bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-3"><h2 className="text-xl font-semibold"><FaBuilding className="inline"/> {companyForm.id ? 'Alterar empresa' : 'Cadastro de empresas'}</h2>{companyForm.id && <button type="button" onClick={() => setCompanyForm(emptyCompanyForm)} className="rounded-full border px-3 py-1 text-xs text-slate-600">Novo cadastro</button>}</div>
                  <input value={companyForm.name} onChange={(e) => setCompanyForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Nome fantasia" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" required />
                  <input value={companyForm.legalName} onChange={(e) => setCompanyForm((prev) => ({ ...prev, legalName: e.target.value }))} placeholder="Razão social" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                  <div className="grid gap-3 sm:grid-cols-2"><input value={companyForm.cnpj} onChange={(e) => setCompanyForm((prev) => ({ ...prev, cnpj: maskCnpj(e.target.value) }))} placeholder="CPF / CNPJ" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /><input value={companyForm.zipCode} onChange={async (e) => { const zipCode = maskCep(e.target.value); setCompanyForm((prev) => ({ ...prev, zipCode })); const digits = zipCode.replace(/\D/g, ''); if (digits.length === 8) { try { const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`); const data = await response.json(); if (!data.erro) setCompanyForm((prev) => ({ ...prev, zipCode, address: data.logradouro || '', neighborhood: data.bairro || '', city: data.localidade || '', state: data.uf || '' })); } catch (error) { /* CEP lookup is optional */ } } }} placeholder="CEP" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></div>
                  <label className="block text-xs text-slate-600">Situação<select value={companyForm.status || 'ACTIVE'} onChange={e=>setCompanyForm(prev=>({...prev,status:e.target.value}))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"><option value="ACTIVE">Ativo</option><option value="INACTIVE">Inativo</option></select></label>
                  <input value={companyForm.address} onChange={(e) => setCompanyForm((prev) => ({ ...prev, address: e.target.value }))} placeholder="Endereço" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                  <div className="grid gap-3 sm:grid-cols-3">{[['number','Número'],['complement','Complemento'],['neighborhood','Bairro'],['city','Cidade'],['state','UF']].map(([key,label]) => <label key={key} className="text-xs text-slate-600">{label}<input value={companyForm[key] || ''} onChange={e=>setCompanyForm(prev=>({...prev,[key]:key==='state'?e.target.value.toUpperCase().slice(0,2):e.target.value}))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>)}</div>
                  <div className="space-y-2"><p className="text-xs font-semibold uppercase text-slate-500">Telefones</p>{companyForm.phones.map((phone, index) => <div key={`phone-${index}`} className="flex gap-2"><input value={phone} onChange={(e) => setCompanyForm((prev) => ({ ...prev, phones: prev.phones.map((item, itemIndex) => itemIndex === index ? maskPhone(e.target.value) : item) }))} placeholder="Telefone" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />{index === companyForm.phones.length - 1 && <button type="button" onClick={() => setCompanyForm((prev) => ({ ...prev, phones: [...prev.phones, ''] }))} className="rounded-xl border px-3 text-emerald-700"><FaPlus /></button>}</div>)}</div>
                  <div className="space-y-2"><p className="text-xs font-semibold uppercase text-slate-500">E-mails</p>{companyForm.emails.map((email, index) => <div key={`email-${index}`} className="flex gap-2"><input type="email" value={email} onChange={(e) => setCompanyForm((prev) => ({ ...prev, emails: prev.emails.map((item, itemIndex) => itemIndex === index ? e.target.value : item) }))} placeholder="E-mail" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />{index === companyForm.emails.length - 1 && <button type="button" onClick={() => setCompanyForm((prev) => ({ ...prev, emails: [...prev.emails, ''] }))} className="rounded-xl border px-3 text-emerald-700"><FaPlus /></button>}</div>)}</div>
                  <textarea value={companyForm.notes} onChange={(e) => setCompanyForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Observações" rows={3} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                  {canManage && <button className="rounded-full bg-emerald-600 px-4 py-2 text-white">{companyForm.id ? 'Salvar alterações' : 'Adicionar empresa'}</button>}
                </form>
                <div className="space-y-2 rounded-2xl border bg-white p-5 shadow-sm">{companies.map((c) => <div key={c.id} className="flex justify-between rounded-xl border p-3"><div><b>{c.name}</b><p className="text-xs text-slate-500">{c.legalName} • {c.cnpj}</p></div><div className="flex gap-2"><button type="button" onClick={() => setSelectedCompany(c)} className="rounded-full border p-2"><FaSearch /></button>{canManage && <button type="button" onClick={() => setCompanyForm(normalizeCompanyForForm(c))} className="rounded-full border p-2 text-emerald-700"><FaEdit /></button>}{canManage && <button type="button" onClick={() => handleDeleteCompany(c.id)} className="rounded-full border border-rose-200 p-2 text-rose-600"><FaTrash /></button>}</div></div>)}</div>
              </div>
            )}

            {settingsSection === 'imports' && (
              <div className="rounded-2xl border bg-white p-5 shadow-sm">
                <h2 className="text-xl font-semibold"><FaFileImport className="inline"/> Importação OFX/Excel e Template</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {canManage && !isOpenFinanceAccount && <button type="button" onClick={triggerFileDialog} disabled={importingOfx || !selectedAccountId} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-700 disabled:opacity-50"><FaFileImport /> {importingOfx ? 'Importando…' : 'Importar OFX/Excel'}</button>}
                  <button type="button" onClick={handleDownloadImportTemplate} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm text-slate-600"><FaFileExcel /> Template Excel</button>
                  {canManage && <button type="button" onClick={handleSyncAccount} disabled={syncingAccount || !selectedAccount || selectedAccount.integrationType !== 'API'} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-700 disabled:opacity-50"><FaSync /> {syncingAccount ? 'Sincronizando…' : 'Sincronizar conta'}</button>}
                </div>
                {isOpenFinanceAccount && <p className="mt-3 inline-flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800"><FaLock /> Importação OFX/Excel e lançamentos manuais estão bloqueados. Use somente a sincronização bancária automática.</p>}
                <p className="mt-3 text-sm text-slate-500">Selecione a conta no módulo financeiro antes de importar ou sincronizar lançamentos.</p>
              </div>
            )}

            {settingsSection === 'expenses' && <ExpensesManager canManage={canManage} />}
          </div>
        )}
      </div>

      {selectedCompany && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4"><div className="max-w-lg rounded-3xl bg-white p-6 shadow-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto"><h3 className="text-xl font-semibold">{selectedCompany.name}</h3><div className="mt-3 space-y-2 text-sm text-slate-600"><p><b>Razão social:</b> {selectedCompany.legalName || '-'}</p><p><b>CNPJ:</b> {selectedCompany.cnpj || '-'}</p><p><b>Endereço:</b> {selectedCompany.address || '-'}</p><p><b>Telefones:</b> {selectedCompany.phones || '-'}</p><p><b>E-mails:</b> {selectedCompany.emails || '-'}</p><p><b>Observações:</b> {selectedCompany.notes || '-'}</p></div><button onClick={() => setSelectedCompany(null)} className="mt-4 rounded-full bg-emerald-600 px-4 py-2 text-white">Fechar</button></div></div>, document.body)}

      {showForm && createPortal((
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={handleCancelForm}
              className="absolute right-4 top-4 inline-flex items-center justify-center rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              <FaTimes className="h-4 w-4" />
              <span className="sr-only">Fechar</span>
            </button>
            <h3 className="text-xl font-semibold text-slate-900">
              {formState.id ? 'Editar conta bancária' : 'Nova conta bancária'}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Informe os dados da conta para habilitar lançamentos e conciliações.
            </p>
            <form className="mt-4 space-y-4" onSubmit={handleSaveAccount}>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-name">
                  Nome interno
                </label>
                <input
                  id="finance-account-name"
                  name="name"
                  value={formState.name}
                  onChange={handleFormChange}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder="Conta principal"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-bank">
                  Banco
                </label>
                <input
                  id="finance-account-bank"
                  name="bankName"
                  value={formState.bankName}
                  onChange={handleFormChange}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder="Banco XYZ"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-agency">
                    Agência
                  </label>
                  <input
                    id="finance-account-agency"
                    name="agencyNumber"
                    value={formState.agencyNumber}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    placeholder="0001"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-number">
                    Conta
                  </label>
                  <input
                    id="finance-account-number"
                    name="accountNumber"
                    value={formState.accountNumber}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    placeholder="12345-6"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-description">
                  Descrição interna
                </label>
                <textarea
                  id="finance-account-description"
                  name="description"
                  value={formState.description}
                  onChange={handleFormChange}
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder="Contexto sobre o uso desta conta bancária"
                />
                <p className="text-xs text-slate-400">
                  Utilize este campo para documentar o objetivo ou detalhes de controle financeiro desta conta.
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-integration">
                  Integração bancária
                </label>
                <select
                  id="finance-account-integration"
                  name="integrationType"
                  value={formState.integrationType}
                  onChange={handleFormChange}
                  disabled={Boolean(formState.id && formState.originalIntegrationType === 'API')}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  {INTEGRATION_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {formState.integrationType === 'API' && (
                <div className="space-y-3 rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
                  <div>
                    <label htmlFor="finance-account-sync-start" className="font-semibold">Capturar movimentações a partir de</label>
                    <input
                      id="finance-account-sync-start"
                      name="integrationSyncStartAt"
                      type="date"
                      max={new Date().toISOString().slice(0, 10)}
                      value={formState.integrationSyncStartAt}
                      onChange={handleFormChange}
                      required
                      className="mt-1 block w-full rounded-xl border border-sky-200 bg-white px-3 py-2 text-slate-700"
                    />
                    <p className="mt-1 text-xs">Todas as movimentações disponíveis desde esta data serão importadas, em páginas.</p>
                  </div>
                  <p className="flex items-center gap-2 font-semibold"><FaLock /> Conexão automática, exclusiva e protegida</p>
                  <p className="mt-1">Depois de salvar, escolha entre autorização Open Finance ou conexão por credenciais, conforme o banco. Login, senha e MFA serão solicitados somente na tela segura do provedor.</p>
                  <p className="mt-1">Os lançamentos entrarão apenas pela sincronização. Valor, data e tipo (crédito ou débito) ficarão bloqueados; somente a descrição poderá ser alterada.</p>
                  <p className="mt-1 text-xs">O Nash não recebe nem armazena credenciais bancárias. Nunca informe tokens, senhas ou segredos neste cadastro.</p>
                </div>
              )}
              {formError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                  {formError}
                </div>
              )}
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleCancelForm}
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingAccount}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                >
                  <FaSave className="h-4 w-4" />
                  {savingAccount
                    ? 'Salvando…'
                    : formState.integrationType === 'API' && formState.originalIntegrationType !== 'API'
                      ? 'Salvar e escolher banco'
                      : 'Salvar conta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ), document.body)}

      {showCategoryManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
                  <FaTags className="h-5 w-5 text-emerald-600" />
                  Gerenciar categorias financeiras
                </h3>
                <p className="text-sm text-slate-500">
                  Cadastre categorias e subcategorias para organizar seus lançamentos e relatórios.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {categoriesLoading && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">Carregando…</span>
                )}
                <button
                  type="button"
                  onClick={handleCloseCategoryManager}
                  className="inline-flex items-center justify-center rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  <FaTimes className="h-4 w-4" />
                  <span className="sr-only">Fechar</span>
                </button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <FaTags className="h-4 w-4 text-emerald-600" />
                    Nova categoria
                  </h4>
                  <form className="mt-3 space-y-3" onSubmit={handleCreateCategory}>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-category-name">
                        Nome
                      </label>
                      <input
                        id="finance-new-category-name"
                        name="name"
                        value={categoryFormState.name}
                        onChange={handleCategoryFormChange}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        placeholder="Ex: Receitas"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-category-color">
                          Cor (opcional)
                        </label>
                        <input
                          id="finance-new-category-color"
                          name="color"
                          value={categoryFormState.color}
                          onChange={handleCategoryFormChange}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          placeholder="#16a34a"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-category-description">
                          Descrição (opcional)
                        </label>
                        <input
                          id="finance-new-category-description"
                          name="description"
                          value={categoryFormState.description}
                          onChange={handleCategoryFormChange}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          placeholder="Resumo da categoria"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={categorySubmitting}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                    >
                      <FaSave className="h-3 w-3" />
                      {categorySubmitting ? 'Salvando…' : 'Adicionar categoria'}
                    </button>
                  </form>
                </div>
                {categoryError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                    {categoryError}
                  </div>
                )}
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Nome</th>
                        <th className="px-3 py-2">Cor</th>
                        <th className="px-3 py-2">Subcategorias</th>
                        <th className="px-3 py-2 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {categories.length === 0 ? (
                        <tr>
                          <td className="px-3 py-3 text-sm text-slate-500" colSpan={4}>
                            Nenhuma categoria cadastrada.
                          </td>
                        </tr>
                      ) : (
                        categories.map((category) => {
                          const subcategoryCount = Array.isArray(category.subcategories)
                            ? category.subcategories.length
                            : 0;
                          return (
                            <tr key={category.id} className="text-sm text-slate-600">
                              <td className="px-3 py-3 font-medium text-slate-700">{category.name}</td>
                              <td className="px-3 py-3">
                                {category.color ? (
                                  <span className="inline-flex items-center gap-2 text-xs">
                                    <span
                                      className="inline-block h-3 w-3 rounded-full"
                                      style={{ backgroundColor: category.color }}
                                    />
                                    <span className="font-mono text-slate-500">{category.color}</span>
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">Sem cor</span>
                                )}
                              </td>
                              <td className="px-3 py-3 text-sm text-slate-500">
                                {subcategoryCount > 0
                                  ? `${subcategoryCount} subcategoria${subcategoryCount > 1 ? 's' : ''}`
                                  : 'Nenhuma subcategoria'}
                              </td>
                              <td className="px-3 py-3 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleEditCategory(category)}
                                    disabled={
                                      categoryUpdatingId === category.id ||
                                      categoryDeletingId === category.id
                                    }
                                    className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
                                  >
                                    <FaEdit className="h-3 w-3" />
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteCategory(category.id)}
                                    disabled={categoryDeletingId === category.id}
                                    className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:opacity-50"
                                  >
                                    <FaTrash className="h-3 w-3" />
                                    {categoryDeletingId === category.id ? 'Removendo…' : 'Excluir'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <FaSitemap className="h-4 w-4 text-emerald-600" />
                    Nova subcategoria
                  </h4>
                  <form className="mt-3 space-y-3" onSubmit={handleCreateSubcategory}>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-subcategory-category">
                        Categoria
                      </label>
                      <select
                        id="finance-new-subcategory-category"
                        name="categoryId"
                        value={subcategoryFormState.categoryId}
                        onChange={handleSubcategoryFormChange}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        required
                      >
                        <option value="">Selecione…</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-subcategory-name">
                        Nome
                      </label>
                      <input
                        id="finance-new-subcategory-name"
                        name="name"
                        value={subcategoryFormState.name}
                        onChange={handleSubcategoryFormChange}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        placeholder="Ex: Receitas recorrentes"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-subcategory-color">
                          Cor (opcional)
                        </label>
                        <input
                          id="finance-new-subcategory-color"
                          name="color"
                          value={subcategoryFormState.color}
                          onChange={handleSubcategoryFormChange}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          placeholder="#0ea5e9"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-subcategory-description">
                          Descrição (opcional)
                        </label>
                        <input
                          id="finance-new-subcategory-description"
                          name="description"
                          value={subcategoryFormState.description}
                          onChange={handleSubcategoryFormChange}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          placeholder="Detalhes da subcategoria"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={subcategorySubmitting || categories.length === 0}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                    >
                      <FaSave className="h-3 w-3" />
                      {subcategorySubmitting ? 'Salvando…' : 'Adicionar subcategoria'}
                    </button>
                  </form>
                </div>
                {subcategoryError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                    {subcategoryError}
                  </div>
                )}
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Subcategoria</th>
                        <th className="px-3 py-2">Categoria</th>
                        <th className="px-3 py-2">Cor</th>
                        <th className="px-3 py-2 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {flattenedSubcategories.length === 0 ? (
                        <tr>
                          <td className="px-3 py-3 text-sm text-slate-500" colSpan={4}>
                            Nenhuma subcategoria cadastrada.
                          </td>
                        </tr>
                      ) : (
                        flattenedSubcategories.map((subcategory) => (
                          <tr key={subcategory.id} className="text-sm text-slate-600">
                            <td className="px-3 py-3 font-medium text-slate-700">{subcategory.name}</td>
                            <td className="px-3 py-3 text-sm text-slate-500">{subcategory.categoryName}</td>
                            <td className="px-3 py-3">
                              {subcategory.color ? (
                                <span className="inline-flex items-center gap-2 text-xs">
                                  <span
                                    className="inline-block h-3 w-3 rounded-full"
                                    style={{ backgroundColor: subcategory.color }}
                                  />
                                  <span className="font-mono text-slate-500">{subcategory.color}</span>
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400">Sem cor</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditSubcategory(subcategory)}
                                  disabled={
                                    subcategoryUpdatingId === subcategory.id ||
                                    subcategoryDeletingId === subcategory.id
                                  }
                                  className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
                                >
                                  <FaEdit className="h-3 w-3" />
                                  Editar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSubcategory(subcategory.id)}
                                  disabled={subcategoryDeletingId === subcategory.id}
                                  className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:opacity-50"
                                >
                                  <FaTrash className="h-3 w-3" />
                                  {subcategoryDeletingId === subcategory.id ? 'Removendo…' : 'Excluir'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCategoryReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6">
          <div className="relative max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Resumo por categoria</h3>
                {categoryReportGeneratedAt && (
                  <p className="mt-1 text-xs text-slate-500">Gerado em {categoryReportGeneratedAt}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {categoryReportLoading && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    <FaSync className="h-3 w-3 animate-spin" /> Carregando…
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleCloseCategoryReport}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  Fechar
                </button>
              </div>
            </div>

            <div className="mt-4 space-y-6">
              {categoryReportError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  {categoryReportError}
                </div>
              ) : categoryReportLoading && !hasCategoryReportData ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Carregando resumo financeiro…
                </div>
              ) : hasCategoryReportData ? (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
                        Total de receitas
                      </p>
                      <p className="mt-2 text-lg font-semibold text-emerald-700">
                        {formatCurrency(categoryReportTotals.credits)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">
                        Total de despesas
                      </p>
                      <p className="mt-2 text-lg font-semibold text-rose-600">
                        {formatCurrency(categoryReportTotals.debits)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Saldo do período
                      </p>
                      <p
                        className={`mt-2 text-lg font-semibold ${
                          categoryReportTotals.balance >= 0
                            ? 'text-emerald-700'
                            : 'text-rose-600'
                        }`}
                      >
                        {formatCurrency(categoryReportTotals.balance)}
                      </p>
                    </div>
                  </div>

                  {(categoryMovementChart || categoryBalanceChart) && (
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      {categoryMovementChart && (
                        <div className="rounded-2xl border border-slate-200 p-4">
                          <h4 className="text-sm font-semibold text-slate-700">
                            Distribuição das movimentações
                          </h4>
                          <div className="mt-4 h-64">
                            <Doughnut data={categoryMovementChart} options={categoryMovementChartOptions} />
                          </div>
                        </div>
                      )}
                      {categoryBalanceChart && (
                        <div className="rounded-2xl border border-slate-200 p-4">
                          <h4 className="text-sm font-semibold text-slate-700">
                            Receitas x despesas por categoria
                          </h4>
                          <div className="mt-4 h-64">
                            <Bar data={categoryBalanceChart} options={categoryBalanceChartOptions} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {topSubcategoryChart && (
                    <div className="rounded-2xl border border-slate-200 p-4">
                      <h4 className="text-sm font-semibold text-slate-700">
                        Subcategorias com maior movimentação
                      </h4>
                      <div className="mt-4 h-72">
                        <Bar data={topSubcategoryChart} options={topSubcategoryChartOptions} />
                      </div>
                    </div>
                  )}

                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-4 py-3">Categoria / Subcategoria</th>
                          <th className="px-4 py-3 text-right">Transações</th>
                          <th className="px-4 py-3 text-right">Receitas</th>
                          <th className="px-4 py-3 text-right">Despesas</th>
                          <th className="px-4 py-3 text-right">Saldo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {categoryReportData.categories.map((category) => {
                          const categoryKey = `category-${category.id ?? 'none'}-${category.name}`;
                          const subcategories = Array.isArray(category.subcategories)
                            ? category.subcategories
                            : [];
                          return (
                            <React.Fragment key={categoryKey}>
                              <tr className="bg-slate-50/60 text-sm text-slate-700">
                                <td className="px-4 py-3 font-semibold">
                                  <span className="inline-flex items-center gap-2">
                                    {category.color && (
                                      <span
                                        className="inline-block h-3 w-3 rounded-full"
                                        style={{ backgroundColor: category.color }}
                                      />
                                    )}
                                    {category.name}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right font-medium">
                                  {category.transactionCount}
                                </td>
                                <td className="px-4 py-3 text-right text-emerald-700">
                                  {formatCurrency(category.totalCredits)}
                                </td>
                                <td className="px-4 py-3 text-right text-rose-600">
                                  {formatCurrency(category.totalDebits)}
                                </td>
                                <td
                                  className={`px-4 py-3 text-right font-semibold ${
                                    category.balance >= 0 ? 'text-emerald-700' : 'text-rose-600'
                                  }`}
                                >
                                  {formatCurrency(category.balance)}
                                </td>
                              </tr>
                              {subcategories.length === 0 ? (
                                <tr key={`${categoryKey}-empty`} className="text-xs text-slate-500">
                                  <td className="px-4 py-3 pl-10" colSpan={5}>
                                    Sem subcategorias cadastradas para esta categoria.
                                  </td>
                                </tr>
                              ) : (
                                subcategories.map((subcategory) => {
                                  const subKey = `subcategory-${category.id ?? 'none'}-${
                                    subcategory.id ?? 'none'
                                  }`;
                                  return (
                                    <tr key={subKey} className="text-xs text-slate-600">
                                      <td className="px-4 py-2 pl-10">
                                        <span className="inline-flex items-center gap-2">
                                          {subcategory.color && (
                                            <span
                                              className="inline-block h-2.5 w-2.5 rounded-full"
                                              style={{ backgroundColor: subcategory.color }}
                                            />
                                          )}
                                          {subcategory.name}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2 text-right">{subcategory.transactionCount}</td>
                                      <td className="px-4 py-2 text-right text-emerald-700">
                                        {formatCurrency(subcategory.totalCredits)}
                                      </td>
                                      <td className="px-4 py-2 text-right text-rose-600">
                                        {formatCurrency(subcategory.totalDebits)}
                                      </td>
                                      <td
                                        className={`px-4 py-2 text-right font-semibold ${
                                          subcategory.balance >= 0
                                            ? 'text-emerald-700'
                                            : 'text-rose-600'
                                        }`}
                                      >
                                        {formatCurrency(subcategory.balance)}
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Nenhuma movimentação encontrada para gerar o relatório.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
